import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../../siteComponents/InfoToolTip.js";
import getCroppedImg from "../../utils/cropImageUtils";
import { formatInvestorPerkAmount } from "../../utils/normalizeInvestorPerks";

import { notifyUser } from "../../utils/notifyUser";

// Component for managing and editing a campaign page, including uploads, tabs, and previews

const normalizeInvestorPerks = (value) => {
    if (Array.isArray(value)) {
        return value.slice(0, 5).map((perk) =>
            typeof perk === "string"
                ? { amount: "", description: perk }
                : {
                    amount: perk?.amount != null ? String(perk.amount) : "",
                    description: perk?.description != null ? String(perk.description) : "",
                },
        );
    }
    if (typeof value === "string" && value.trim()) {
        try {
            return normalizeInvestorPerks(JSON.parse(value));
        } catch {
            return [{ amount: "", description: value }];
        }
    }
    return [{ amount: "", description: "" }];
};

const getEdgarFilingUrlError = (value) => {
    const filingUrl = String(value || "").trim();
    if (!filingUrl) return "";

    try {
        const parsedUrl = new URL(filingUrl);
        const isSecDomain =
            parsedUrl.hostname === "sec.gov" ||
            parsedUrl.hostname.endsWith(".sec.gov");
        const isEdgarFiling = /^\/Archives\/edgar\/data\//i.test(
            parsedUrl.pathname,
        );

        if (
            parsedUrl.protocol !== "https:" ||
            !isSecDomain ||
            !isEdgarFiling
        ) {
            return "Enter a valid SEC EDGAR filing link beginning with https://www.sec.gov/Archives/edgar/data/.";
        }
    } catch {
        return "Enter a valid SEC EDGAR filing link beginning with https://www.sec.gov/Archives/edgar/data/.";
    }

    return "";
};

const CampaignAboutPage = ({ user }) => {
    const [minAmount, setMinAmount] = useState("");
    const navigate = useNavigate();
    const [applicationId, setApplicationId] = useState(null);

    // State for modal previews of images and PDFs
    const [modalOpen, setModalOpen] = useState(false);
    const [activePdf, setActivePdf] = useState(null);
    const [activeImage, setActiveImage] = useState(null);
    const [, setIsInitializing] = useState(true);
    const [campaignId, setCampaignId] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [draftSubmitted, setDraftSubmitted] = useState(false);
    const [, setSaveProgress] = useState(false);
    const [status, setStatus] = useState(null);
    const [disabledEditEnabled, setDisabledEditEnabled] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState("");
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppingImage, setCroppingImage] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [campaignName, setCampaignName] = useState("");
    const [campaignDescription, setCampaignDescription] = useState("");
    const [campaignStartDate, setCampaignStartDate] = useState("");
    const [campaignEndDate, setCampaignEndDate] = useState("");
    const [, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const maxDescriptionLength = 200;
    const maxLongFormLength = 5000;
    const [overviewPitch, setOverviewPitch] = useState("");
    const [callToAction, setCallToAction] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [companyCity, setCompanyCity] = useState("");
    const [companyState, setCompanyState] = useState("");
    const [investorsInfo, setInvestorsInfo] = useState("");
    const [websiteLink, setWebsiteLink] = useState("");
    const [facebookLink, setFacebookLink] = useState("");
    const [linkedinLink, setLinkedinLink] = useState("");
    const [twitterLink, setTwitterLink] = useState("");
    const [tiktokLink, setTiktokLink] = useState("");
    const [youtubeLink, setYoutubeLink] = useState("");
    const [instagramLink, setInstagramLink] = useState("");
    const [investmentAmount, setInvestmentAmount] = useState("");
    const [goalHit, setGoalHit] = useState("");
    const [maxAmount, setMaxAmount] = useState("");
    const [unitPrice, setUnitPrice] = useState("");
    const [minimumRaised, setMinimumRaised] = useState("");
    const emptyLegalUploads = {
        edgar_filing_url: "",
        subscription_agreement: [],
        financials: [],
        risks: [""],
        use_of_funds: "",
        investment_valuation: "",
        investor_perks: [{ amount: "", description: "" }],
    };
    const [legalUploads, setLegalUploads] = useState(emptyLegalUploads);
    const [showAllCampaignPerks, setShowAllCampaignPerks] = useState(false);
    const [edgarFilingUrlError, setEdgarFilingUrlError] = useState("");
    const [legalFiles, setLegalFiles] = useState({ subscription_agreement: [], financials: [] });
    const [legalUploading, setLegalUploading] = useState({ subscription_agreement: false, financials: false });
    const [pitchProblem, setPitchProblem] = useState("");
    const [pitchSolution, setPitchSolution] = useState("");
    const [pitchSlides, setPitchSlides] = useState([
        { overview: "", file: "" },
    ]);
    const maxSlides = 8;

    const [founders, setFounders] = useState([
        { name: "", about: "", linkedin: "" },
    ]);
    const maxFounders = 4;

    const [updates, setUpdates] = useState([]);
    const [newUpdateTitle, setNewUpdateTitle] = useState("");
    const [newUpdate, setNewUpdate] = useState("");
    const [newUpdateUrl, setNewUpdateUrl] = useState("");
    const [updateMedia, setUpdateMedia] = useState(null);
    const [updateMediaUploading, setUpdateMediaUploading] = useState(false);
    const [editingUpdateIdx, setEditingUpdateIdx] = useState(null);
    const [editingUpdateTitle, setEditingUpdateTitle] = useState("");
    const [editingUpdateText, setEditingUpdateText] = useState("");
    const [editingUpdateUrl, setEditingUpdateUrl] = useState("");
    const updateFileInputRef = useRef(null);

    const handleSlideChange = (idx, value) => {
        const updated = [...pitchSlides];
        updated[idx].overview = value;
        setPitchSlides(updated);
    };

    const addSlide = () => {
        if (pitchSlides.length < maxSlides) {
            setPitchSlides([...pitchSlides, { overview: "", file: "" }]);
        }
    };

    const removeSlide = () => {
        if (pitchSlides.length > 1) {
            setPitchSlides(pitchSlides.slice(0, -1));
        }
    };

    const handleFounderChange = (idx, field, value) => {
        const updated = [...founders];
        updated[idx][field] = value;
        setFounders(updated);
    };

    const addFounder = () => {
        if (founders.length < maxFounders) {
            setFounders([
                ...founders,
                { name: "", about: "", linkedin: "", file: "" },
            ]);
        }
    };

    const removeFounder = () => {
        if (founders.length > 1) {
            setFounders(founders.slice(0, -1));
        }
    };

    const fileInputRef = useRef(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleBoxClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Drag and Drop Handlers for Background Image
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file && file.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onload = () => {
                    setCroppingImage(reader.result);
                    setShowCropper(true);
                };
                reader.readAsDataURL(file);
            } else {
                notifyUser(null, "error", "Please drop a valid image file.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const uploadPitchSlide = async (index, file) => {
        if (!file || !campaignId) return;
        // Only allow .pdf, .xls, .docx
        const allowedExtensions = [".pdf", ".xls", ".docx"];
        const fileName = file?.name?.toLowerCase() || "";
        const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
        if (!isAllowed) {
            notifyUser(null, "error", "Only PDF, XLS, and DOCX files are allowed for Pitch Deck.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        const formData = new FormData();
        formData.append("pitchSlide", file);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-pitch-slide`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            const newSlides = [...pitchSlides];
            // Construct full URL for immediate preview
            newSlides[index].file =
                `${process.env.REACT_APP_BACKEND_URL}/uploads/${res.data.path}`;
            setPitchSlides(newSlides);
        } catch (err) {
            console.error("Pitch upload error:", err);
            notifyUser(null, "error", "failed to upload pitch slide", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const uploadFounderHeadshot = async (index, file) => {
        if (!file || !campaignId) return;

        const formData = new FormData();
        formData.append("teamHeadshot", file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-team-headshot`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            const updatedFounders = [...founders];
            // Construct full URL for immediate preview
            updatedFounders[index].file =
                `${process.env.REACT_APP_BACKEND_URL}/uploads/${res.data.path}`;
            setFounders(updatedFounders);
        } catch (err) {
            console.error(
                "Headshot upload error:",
                err.response?.data || err.message,
            );
            notifyUser(null, "error", "Failed to upload headshot.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Background Image
    const handleCropSave = async () => {
        if (!croppingImage || !croppedAreaPixels || !campaignId) return;
        const croppedBlob = await getCroppedImg(
            croppingImage,
            croppedAreaPixels,
        );
        const previewUrl = URL.createObjectURL(croppedBlob);
        setBackgroundImage(previewUrl);
        const formData = new FormData();
        formData.append("backgroundImage", croppedBlob, "cropped.jpg");
        formData.append("user_id", user?.id);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-background`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            setBackgroundImage(
                `${process.env.REACT_APP_BACKEND_URL
                }/uploads/${response.data.path.replace(/\\/g, "/")}`,
            );

            setShowCropper(false);
            notifyUser(null, "success", "Background image uploaded successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            setShowCropper(false);
        } catch (err) {
            console.error("Upload error:", err.response?.data || err.message);
            notifyUser(null, "error", "Failed to upload cropped image.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Legal docs: stage files locally — uploaded on Save/Submit
    const handleLegalUpload = (docType, files) => {
        if (!files || files.length === 0) return;

        if (docType === "subscription_agreement" || docType === "financials") {
            // Stage files locally - will be uploaded when Save is clicked
            const fileArr = Array.from(files);
            setLegalFiles((prev) => ({
                ...prev,
                [docType]: [...(Array.isArray(prev[docType]) ? prev[docType] : []), ...fileArr],
            }));
        } else {
            setLegalFiles((prev) => ({ ...prev, [docType]: files[0] }));
            setLegalUploads((prev) => ({ ...prev, [docType]: "" }));
        }
    };

    // Upload legal files immediately to server
    const uploadLegalFiles = async (docType, files) => {
        if (!campaignId || !files || files.length === 0) return;
        // Only allow .pdf, .xls, .docx
        const allowedExtensions = [".pdf", ".xls", ".docx"];
        const validFiles = [];
        for (const file of files) {
            const fileName = file?.name?.toLowerCase() || "";
            const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
            if (!isAllowed) {
                notifyUser(null, "error", `${file.name}: Only PDF, XLS, and DOCX files are allowed for Form C.`, "toast", null, null, null, null, null, false, false, "top-center");
                continue;
            }
            validFiles.push(file);
        }
        if (validFiles.length === 0) {
            notifyUser(null, "error", "No valid files to upload.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        setLegalUploading((prev) => ({ ...prev, [docType]: true }));
        try {
            const formData = new FormData();
            validFiles.forEach((file) => formData.append("legalDoc", file));
            formData.append("docType", docType);
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-legal`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            if (response.data?.paths) {
                // Add the new file paths to the uploaded files state
                setLegalUploads((prev) => ({
                    ...prev,
                    [docType]: [
                        ...(prev[docType] || []),
                        ...response.data.paths,
                    ],
                }));
                // Clear the selected files since they're now uploaded
                setLegalFiles((prev) => ({
                    ...prev,
                    [docType]: (docType === "subscription_agreement" || docType === "financials") ? [] : null,
                }));
                notifyUser(null, "success", `${validFiles.length} ${docType} file(s) uploaded successfully`, "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch (error) {
            console.error("Error uploading legal files:", error);
            notifyUser(null, "error", "Failed to upload legal files", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLegalUploading((prev) => ({ ...prev, [docType]: false }));
        }
    };

    // Function to handle update media selection
    const handleUpdateMediaSelect = () => {
        if (updateFileInputRef.current) {
            updateFileInputRef.current.click();
        }
    };

    // Function to upload update media
    const uploadUpdateMedia = async (file) => {
        if (!file || !campaignId) return;

        setUpdateMediaUploading(true);
        const formData = new FormData();
        formData.append("updateMedia", file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-update-media`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            // Set the media path for immediate preview
            const mediaPath = `${process.env.REACT_APP_BACKEND_URL}/uploads/${res.data.path}`;
            setUpdateMedia(mediaPath);
            setUpdateMediaUploading(false);

            return mediaPath;
        } catch (err) {
            console.error(
                "Update media upload error:",
                err.response?.data || err.message,
            );
            notifyUser(null, "error", "Failed to upload media for update.", "toast", null, null, null, null, null, false, false, "top-center");
            setUpdateMediaUploading(false);
            return null;
        }
    };

    // Function to handle posting an update with media
    const handlePostUpdate = async (mediaPath = null) => {
        // Only proceed if the update has a title, body, link, or attachment.
        if (
            !newUpdateTitle.trim() &&
            !newUpdate.trim() &&
            !newUpdateUrl.trim() &&
            !mediaPath &&
            !updateMedia
        ) {
            return;
        }

        const updateObject = {
            title: newUpdateTitle.trim(),
            text: newUpdate.trim(),
            url: newUpdateUrl.trim(),
            media: mediaPath || updateMedia,
            timestamp: new Date().toISOString(),
        };

        // Add the new update to the updates array
        setUpdates([updateObject, ...updates]);

        // Reset form
        setNewUpdateTitle("");
        setNewUpdate("");
        setNewUpdateUrl("");
        setUpdateMedia(null);
    };

    // Helper function to determine media type
    const getMediaType = (url) => {
        if (!url) return null;

        const extension = url.split(/[?#]/)[0].split(".").pop().toLowerCase();

        if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
            return "image";
        } else if (["mp4", "webm", "ogg"].includes(extension)) {
            return "video";
        } else if (["pdf"].includes(extension)) {
            return "document";
        }

        return "unknown";
    };

    const normalizeAssetUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        if (/^https?:\/\//i.test(url)) return url;
        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${url.replace(/^\/?uploads\//, "")}`;
    };

    const normalizeExternalUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    };

    const getLinkLabel = (url) => {
        try {
            const parsedUrl = new URL(normalizeExternalUrl(url));
            return parsedUrl.hostname.replace(/^www\./, "");
        } catch {
            return url;
        }
    };

    const campaignLinks = [
        { label: "Website", icon: "fas fa-globe", url: websiteLink, className: "border-navy text-navy hover:bg-[#f0f6fb]" },
        { label: "Facebook", icon: "fab fa-facebook-f", url: facebookLink, className: "border-[#3b5998] text-[#3b5998] hover:bg-[#f0f2f5]" },
        { label: "LinkedIn", icon: "fab fa-linkedin-in", url: linkedinLink, className: "border-[#0077b5] text-[#0077b5] hover:bg-[#f3f8fa]" },
        { label: "Twitter", icon: "fab fa-twitter", url: twitterLink, className: "border-[#1da1f2] text-[#1da1f2] hover:bg-[#f3f8fa]" },
        { label: "TikTok", icon: "fab fa-tiktok", url: tiktokLink, className: "border-black text-black hover:bg-gray-50" },
        { label: "YouTube", icon: "fab fa-youtube", url: youtubeLink, className: "border-[#ff0000] text-[#ff0000] hover:bg-red-50" },
        { label: "Instagram", icon: "fab fa-instagram", url: instagramLink, className: "border-pink-500 text-pink-500 hover:bg-pink-50" },
    ].filter((link) => link.url);

    const campaignLinkWidthClass =
        campaignLinks.length === 1
            ? "md:w-full"
            : campaignLinks.length === 2 || campaignLinks.length === 4
                ? "md:w-[calc(50%-0.5rem)]"
                : "md:w-[calc(33.333%-0.667rem)]";

    // Function to delete updateMedia before posting
    const handleCancelUpdateMedia = () => {
        setUpdateMedia(null);
    };

    // Function to delete an existing update
    const handleDeleteUpdate = (index) => {
        // Create a new array excluding the update at the specified index
        const updatedList = updates.filter((_, idx) => idx !== index);
        setUpdates(updatedList);
        notifyUser(null, "success", "Update deleted successfully", "toast", null, null, null, null, null, false, false, "top-center");
    };

    // Function to start editing an update
    const handleEditUpdate = (index) => {
        setEditingUpdateIdx(index);
        setEditingUpdateTitle(updates[index].title || "");
        setEditingUpdateText(
            updates[index].description || updates[index].text || "",
        );
        setEditingUpdateUrl(updates[index].url || "");
    };

    // Function to save edited update
    const handleSaveEditUpdate = (index) => {
        if (index !== null && index >= 0 && index < updates.length) {
            const updatedList = [...updates];
            updatedList[index] = {
                ...updatedList[index],
                title: editingUpdateTitle.trim(),
                text: editingUpdateText.trim(),
                url: editingUpdateUrl.trim(),
                // Keep the original timestamp and media
            };
            setUpdates(updatedList);
            setEditingUpdateIdx(null);
            setEditingUpdateTitle("");
            setEditingUpdateText("");
            setEditingUpdateUrl("");
            notifyUser(null, "success", "Update edited successfully", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Function to cancel editing
    const handleCancelEditUpdate = () => {
        setEditingUpdateIdx(null);
        setEditingUpdateTitle("");
        setEditingUpdateText("");
        setEditingUpdateUrl("");
    };

    // Delete a legal document permanently from database
    const handleDeleteLegalDocument = async (docType, fileIndex) => {
        if (submitted && !isEditing) {
            notifyUser(null, "error", "Cannot delete files while campaign is under review", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        if (!campaignId) {
            notifyUser(null, "error", "No campaign ID found", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Get the file path from the legalUploads array
        const filePath = legalUploads[docType]?.[fileIndex];
        if (!filePath) {
            notifyUser(null, "error", "File not found in legal uploads", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/legal/${docType}`,
                {
                    data: { filePath: filePath },
                    withCredentials: true,
                },
            );

            // Update the local state to remove the deleted file
            setLegalUploads((prev) => ({
                ...prev,
                [docType]: prev[docType].filter((_, i) => i !== fileIndex),
            }));

            notifyUser(null, "success", "Legal document deleted successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            // Provide more specific error messages
            if (error.response?.status === 404) {
                notifyUser(null, "error", "Document not found or already deleted", "toast", null, null, null, null, null, false, false, "top-center");
            } else if (error.response?.status === 400) {
                notifyUser(null, "error", "Invalid request: " +
                    (error.response?.data?.error || "Bad request"), "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to delete legal document: " +
                    (error.response?.data?.error || error.message), "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const getLegalDocumentUrl = (document) => {
        const path = typeof document === "string" ? document : document?.file;
        if (!path) return "";
        return /^https?:\/\//i.test(path)
            ? path
            : `${process.env.REACT_APP_BACKEND_URL}/uploads/${path.replace(/^\/?uploads\//, "")}`;
    };

    const getLegalDocumentName = (document, fallback) => {
        if (document?.name) return document.name;
        const path = typeof document === "string" ? document : document?.file;
        return path?.split(/[\\/]/).pop() || fallback;
    };

    const validateEdgarFilingUrl = () => {
        const error = getEdgarFilingUrlError(
            legalUploads.edgar_filing_url,
        );
        setEdgarFilingUrlError(error);

        if (error) {
            notifyUser(
                null,
                "error",
                error,
                "toast",
                null,
                null,
                null,
                null,
                null,
                false,
                false,
                "top-center",
            );
            return false;
        }

        return true;
    };

    // pre-filling campaign data (fetch only, no creation)
    // Effect to reset activeTab if it's on CHAT tab and status is not approved
    useEffect(() => {
        if (activeTab === 6 && status !== "approved") {
            setActiveTab(0);
        }
    }, [status, activeTab]);

    useEffect(() => {
        const fetchCampaignAndPrefill = async () => {
            const userId = user?.id;
            const userEmail = user?.email;
            if (!userId || !userEmail) return;

            setIsInitializing(true);
            try {
                // Try to fetch campaign data first
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${userId}`,
                    { withCredentials: true },
                );
                const data = res.data;
                setApplicationId(data.application_id);
                setStatus(data.status || null);
                setDraftSubmitted(data.draft_submitted === 1);
                setDisabledEditEnabled(data.disabled_edit_enabled === 1);
                setSaveProgress(data.save_progress === 1);
                setCampaignId(data.id);
                setSubmitted(data.submitted === 1);
                setCampaignName(data.campaign_name || "");
                setCampaignDescription(data.campaign_description || "");
                setCompanyName(data.company_name || "");
                setCompanyCity(data.company_city || "");
                setCompanyState(data.company_state || "");
                setInvestorsInfo(data.investors_info || "");
                setWebsiteLink(data.website_link || "");
                setOverviewPitch(data.overview_pitch || "");
                setCallToAction(data.call_to_action || "");
                setPitchProblem(data.pitch_problem || "");
                setPitchSolution(data.pitch_solution || "");
                setPitchSlides(
                    (data.pitch_slides || [{ overview: "", file: "" }]).map(
                        (slide) => ({
                            ...slide,
                            file: slide.file || "",
                        }),
                    ),
                );
                setFounders(
                    data.founders || [
                        { name: "", about: "", linkedin: "", file: "" },
                    ],
                );

                // Handle updates in both old string format and new object format
                if (data.updates) {
                    const formattedUpdates = Array.isArray(data.updates)
                        ? data.updates.map((update) => {
                            // If update is a string (old format), convert it to object format
                            if (typeof update === "string") {
                                return {
                                    text: update,
                                    media: null,
                                    timestamp: new Date().toISOString(),
                                };
                            }
                            // If already in object format, use as is
                            return update;
                        })
                        : [];
                    setUpdates(formattedUpdates);
                } else {
                    setUpdates([]);
                }

                setFacebookLink(data.facebook_link || "");
                setLinkedinLink(data.linkedin_link || "");
                setTwitterLink(data.twitter_link || "");
                setTiktokLink(data.tiktok_link || "");
                setYoutubeLink(data.youtube_link || "");
                setInstagramLink(data.instagram_link || "");
                setInvestmentAmount(data.investment_amount || "");
                // Prefill minAmount from investment_minimum or investment_min
                if (data.investment_minimum !== undefined && data.investment_minimum !== null) {
                    setMinAmount(data.investment_minimum.toString());
                } else if (data.investment_min !== undefined && data.investment_min !== null) {
                    setMinAmount(data.investment_min.toString());
                } else {
                    setMinAmount("");
                }
                // Prefill campaign_start_date, max_amount, unit_price
                setCampaignStartDate(data.campaign_start_date ? data.campaign_start_date.split("T")[0] : "");
                setMaxAmount(data.max_amount !== undefined && data.max_amount !== null ? data.max_amount.toString() : "");
                setUnitPrice(data.unit_price !== undefined && data.unit_price !== null ? data.unit_price.toString() : "");
                // Prefill Goal Hit and Minimum Raised from fundRaisingGoal if available
                let goalValue = 0;
                if (typeof data.fundRaisingGoal === "number") {
                    goalValue = data.fundRaisingGoal;
                } else if (
                    typeof data.fundRaisingGoal === "string" &&
                    data.fundRaisingGoal.trim() !== ""
                ) {
                    goalValue = parseFloat(data.fundRaisingGoal);
                }
                setGoalHit(goalValue ? goalValue.toString() : "");
                if (data.minimum_raised !== undefined && data.minimum_raised !== null) {
                    setMinimumRaised(data.minimum_raised.toString());
                } else {
                    setMinimumRaised("");
                }                setBackgroundImage(data.campaign_background_image || "");
                setCampaignEndDate(data.end_date || "");

                if (data.legal_uploads) {
                    // Normalize form_c: always array, split on comma/newline, trim, remove empty
                    let formC = data.legal_uploads.form_c;
                    if (Array.isArray(formC)) {
                        // already array
                    } else if (typeof formC === "string") {
                        // split on comma, newline, or both
                        formC = formC
                            .split(/[\n,]+/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                    } else if (formC) {
                        formC = [formC];
                    } else {
                        formC = [];
                    }
                    let subscriptionAgreement = data.legal_uploads.subscription_agreement;
                    if (Array.isArray(subscriptionAgreement)) {
                        // already array
                    } else if (typeof subscriptionAgreement === "string") {
                        subscriptionAgreement = subscriptionAgreement
                            .split(/[\n,]+/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                    } else if (subscriptionAgreement) {
                        subscriptionAgreement = [subscriptionAgreement];
                    } else {
                        subscriptionAgreement = [];
                    }
                    let financials = data.legal_uploads.financials;
                    if (Array.isArray(financials)) {
                        // already array
                    } else if (typeof financials === "string") {
                        financials = financials
                            .split(/[\n,]+/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                    } else if (financials) {
                        financials = [financials];
                    } else {
                        financials = [];
                    }
                    const normalizedLegalUploads = {
                        ...emptyLegalUploads,
                        ...data.legal_uploads,
                        form_c: formC,
                        subscription_agreement: subscriptionAgreement,
                        financials,
                        risks: Array.isArray(data.legal_uploads.risks) && data.legal_uploads.risks.length
                            ? data.legal_uploads.risks.slice(0, 10)
                            : [""],
                        investment_valuation:
                            data.legal_uploads.investment_valuation || data.valuation || "",
                        investor_perks: normalizeInvestorPerks(
                            data.legal_uploads.investor_perks || data.investor_perks,
                        ),
                    };
                    setLegalUploads(normalizedLegalUploads);
                } else {
                    setLegalUploads({
                        ...emptyLegalUploads,
                        investment_valuation: data.valuation || "",
                        investor_perks: normalizeInvestorPerks(data.investor_perks),
                    });
                }

                // If a draft was submitted by the entrepreneur, load draft values into state
                // so the read-only view shows what they submitted (not the live approved data)
                if (
                    data.draft_submitted === 1 &&
                    (data.status === "approved" || data.status === "disabled")
                ) {
                    try {
                        const draftRes = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${data.id}/with-draft`,
                            { withCredentials: true },
                        );
                        const draft = draftRes.data?.draft;
                        if (draft) {
                            if (draft.campaign_name) setCampaignName(draft.campaign_name);
                            if (draft.campaign_description) setCampaignDescription(draft.campaign_description);
                            if (draft.company_name) setCompanyName(draft.company_name);
                            if (draft.company_city) setCompanyCity(draft.company_city);
                            if (draft.company_state) setCompanyState(draft.company_state);
                            if (draft.investors_info !== undefined) setInvestorsInfo(draft.investors_info || "");
                            if (draft.overview_pitch) setOverviewPitch(draft.overview_pitch);
                            if (draft.call_to_action) setCallToAction(draft.call_to_action);
                            if (draft.pitch_problem) setPitchProblem(draft.pitch_problem);
                            if (draft.pitch_solution) setPitchSolution(draft.pitch_solution);
                            if (Array.isArray(draft.pitch_slides) && draft.pitch_slides.length > 0) {
                                setPitchSlides(draft.pitch_slides.map((s) => ({ ...s, file: s.file || "" })));
                            }
                            if (Array.isArray(draft.founders) && draft.founders.length > 0) {
                                setFounders(draft.founders);
                            }
                            if (Array.isArray(draft.updates) && draft.updates.length > 0) {
                                setUpdates(draft.updates.map((u) => typeof u === "string" ? { text: u, media: null, timestamp: new Date().toISOString() } : u));
                            }
                            if (draft.facebook_link !== undefined) setFacebookLink(draft.facebook_link || "");
                            if (draft.linkedin_link !== undefined) setLinkedinLink(draft.linkedin_link || "");
                            if (draft.twitter_link !== undefined) setTwitterLink(draft.twitter_link || "");
                            if (draft.tiktok_link !== undefined) setTiktokLink(draft.tiktok_link || "");
                            if (draft.youtube_link !== undefined) setYoutubeLink(draft.youtube_link || "");
                            if (draft.instagram_link !== undefined) setInstagramLink(draft.instagram_link || "");
                            if (draft.max_amount != null) setMaxAmount(draft.max_amount.toString());
                            if (draft.unit_price != null) setUnitPrice(draft.unit_price.toString());
                            const draftMin = draft.investment_minimum ?? draft.investment_min;
                            if (draftMin != null) setMinAmount(draftMin.toString());
                            if (draft.minimum_raised != null) setMinimumRaised(draft.minimum_raised.toString());
                            const draftGoal = draft.fund_raising_goal ?? draft.goal_amount ?? draft.fundRaisingGoal;
                            if (draftGoal != null) setGoalHit(draftGoal.toString());
                            if (draft.campaign_start_date) setCampaignStartDate(draft.campaign_start_date.split("T")[0]);
                            if (draft.end_date) setCampaignEndDate(draft.end_date.split("T")[0]);
                            if (draft.legal_uploads) {
                                const draftFormC = Array.isArray(draft.legal_uploads.form_c)
                                    ? draft.legal_uploads.form_c
                                    : draft.legal_uploads.form_c ? [draft.legal_uploads.form_c] : [];
                                const draftSA = Array.isArray(draft.legal_uploads.subscription_agreement)
                                    ? draft.legal_uploads.subscription_agreement
                                    : draft.legal_uploads.subscription_agreement ? [draft.legal_uploads.subscription_agreement] : [];
                                const draftFinancials = Array.isArray(draft.legal_uploads.financials)
                                    ? draft.legal_uploads.financials
                                    : draft.legal_uploads.financials ? [draft.legal_uploads.financials] : [];
                                setLegalUploads({
                                    ...emptyLegalUploads,
                                    ...draft.legal_uploads,
                                    form_c: draftFormC,
                                    subscription_agreement: draftSA,
                                    financials: draftFinancials,
                                    risks: Array.isArray(draft.legal_uploads.risks) && draft.legal_uploads.risks.length
                                        ? draft.legal_uploads.risks.slice(0, 10)
                                        : [""],
                                    investment_valuation:
                                        draft.legal_uploads.investment_valuation || data.valuation || "",
                                    investor_perks: normalizeInvestorPerks(
                                        draft.legal_uploads.investor_perks || data.investor_perks,
                                    ),
                                });
                            }
                        }
                    } catch (draftErr) {
                        // Non-fatal — just keep original campaign data
                        console.error("Failed to load pending draft:", draftErr);
                    }
                }
            } catch (err) {
                // If no campaign found, prefill from entrepreneur info
                if (err.response?.status === 404 && userEmail) {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL
                            }/api/auth/entrepreneur-info?email=${encodeURIComponent(
                                userEmail,
                            )}`,
                            { withCredentials: true },
                        );
                        const data = res.data;
                        console.log("[Prefill] Entrepreneur info:", data);
                        setCompanyName(data.companyName || "");
                        setCompanyCity(data.city || "");
                        setCompanyState(data.state || "");
                        // Prefill Goal Hit and Minimum Raised from fundRaisingGoal if available
                        let goalValue = 0;
                        if (typeof data.fundRaisingGoal === "number") {
                            goalValue = data.fundRaisingGoal;
                        } else if (
                            typeof data.fundRaisingGoal === "string" &&
                            data.fundRaisingGoal.trim() !== ""
                        ) {
                            goalValue = parseFloat(data.fundRaisingGoal);
                        }
                        setGoalHit(goalValue ? goalValue.toString() : "");
                        setMinimumRaised(
                            goalValue ? (goalValue / 2).toString() : "",
                        );
                    } catch (e) {
                        console.error(
                            "[Prefill] Error fetching entrepreneur info:",
                            e,
                        );
                    }
                } else {
                    console.error("Error loading campaign:", err);
                }
            } finally {
                setIsInitializing(false);
            }
        };

        fetchCampaignAndPrefill();
    }, [user]);

    // Placeholder for initializeCampaign to resolve linter error
    const initializeCampaign = async () => {
        // TODO: Implement campaign initialization logic
        return Date.now();

    };

    const validateGoalHitAgainstMaxAmount = () => {
        if (goalHit === "" || maxAmount === "") {
            return true;
        }

        const numericGoalHit = Number(goalHit);
        const numericMaxAmount = Number(maxAmount);

        if (
            Number.isFinite(numericGoalHit) &&
            Number.isFinite(numericMaxAmount) &&
            numericGoalHit >= numericMaxAmount
        ) {
            notifyUser(null, "error", "Maximum Amount must be greater than Goal Hit.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        return true;
    };

    // Saving data in each sections(About, Pitch, Team, Updates, Socials, Chat)
    const handleSaveSection = async () => {
        let payload = null;
        let currentId = campaignId;
        try {
            if (!validateEdgarFilingUrl()) return;

            if (!currentId) {
                currentId = await initializeCampaign();
                if (!currentId) return;
            }
            if (!applicationId) {
                console.error("Missing applicationId, cannot save without it");
                notifyUser(null, "error", "Application ID is missing, cannot save. Please try reloading the page.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!maxAmount) {
                notifyUser(null, "error", "Please enter the maximum amount before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!minAmount) {
                notifyUser(null, "error", "Please enter the minimum investment amount before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!campaignStartDate) {
                notifyUser(null, "error", "Please enter the campaign start date before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!campaignEndDate) {
                notifyUser(null, "error", "Please enter the campaign end date before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!unitPrice) {
                notifyUser(null, "error", "Please enter the unit price before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!goalHit) {
                notifyUser(null, "error", "Please enter the fundraising goal before saving.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!validateGoalHitAgainstMaxAmount()) {
                return;
            }
            payload = {
                user_id: user.id,
                application_id: applicationId,
                save_progress: 1,
                goalHit: goalHit,
                campaign_name: campaignName,
                campaign_description: campaignDescription,
                campaign_start_date: campaignStartDate,
                max_amount: maxAmount,
                unit_price: unitPrice,
                investment_min: minAmount,
                minimum_raised: minimumRaised,
                legal_uploads: {
                    ...legalUploads,
                    edgar_filing_url: String(
                        legalUploads.edgar_filing_url || "",
                    ).trim(),
                    investor_perks: normalizeInvestorPerks(legalUploads.investor_perks)
                        .filter((perk) => perk.amount.trim() || perk.description.trim())
                        .slice(0, 5),
                },

            };
            switch (activeTab) {
                // ABOUT
                case 0:
                    if (
                        !companyName ||
                        !companyCity ||
                        !companyState ||
                        !investorsInfo.trim() ||
                        !overviewPitch ||
                        !callToAction
                    ) {
                        notifyUser(null, "error", "Please fill all required fields in the ABOUT section.", "toast", null, null, null, null, null, false, false, "top-center");
                        return;
                    }
                    payload = {
                        ...payload,
                        company_name: companyName,
                        company_city: companyCity,
                        company_state: companyState,
                        investors_info: investorsInfo,
                        overview_pitch: overviewPitch,
                        call_to_action: callToAction,
                        end_date: campaignEndDate,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                // PITCH
                case 1:
                    if (
                        !pitchProblem ||
                        !pitchSolution ||
                        pitchSlides.some((s) => !s.overview || !s.file)
                    ) {
                        notifyUser(null, "error", "Please fill all fields and upload slides in the PITCH section.", "toast", null, null, null, null, null, false, false, "top-center");
                        return;
                    }
                    payload = {
                        ...payload,
                        pitch_problem: pitchProblem,
                        pitch_solution: pitchSolution,
                        pitch_slides: pitchSlides,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                // TEAM
                case 2:
                    if (founders.some((f) => !f.name || !f.about || !f.file)) {
                        notifyUser(null, "error", "Please complete all fields and upload headshots for each founder.", "toast", null, null, null, null, null, false, false, "top-center");
                        return;
                    }
                    payload = {
                        ...payload,
                        founders: founders,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                // UPDATES
                case 3:
                    payload = {
                        ...payload,
                        updates: updates,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                // SOCIALS
                case 4:
                    payload = {
                        ...payload,
                        facebook_link: facebookLink,
                        linkedin_link: linkedinLink,
                        twitter_link: twitterLink,
                        tiktok_link: tiktokLink,
                        youtube_link: youtubeLink,
                        instagram_link: instagramLink,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                // LEGAL & FINANCIALS
                case 5: {
                    let uploads = { ...legalUploads };

                    // --- Handle reviewed/audited financials (multiple) ---
                    if (
                        Array.isArray(legalFiles.financials) &&
                        legalFiles.financials.length > 0
                    ) {
                        setLegalUploading((prev) => ({
                            ...prev,
                            financials: true,
                        }));
                        const formData = new FormData();
                        legalFiles.financials.forEach((file) =>
                            formData.append("legalDoc", file),);
                        formData.append("docType", "financials");
                        formData.append("user_id", user?.id);
                        try {
                            const res = await axios.post(
                                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${currentId}/upload-legal`,
                                formData,
                                {
                                    headers: {
                                        "Content-Type": "multipart/form-data",
                                    },
                                    withCredentials: true,
                                },
                            );
                            // Always treat as array
                            const paths = Array.isArray(res.data?.paths)
                                ? res.data.paths.map((p) =>
                                    p.replace(/\\/g, "/"),)
                                : [];
                            // Merge with any existing uploads (normalize to array)
                            let prevFormC = uploads.financials;
                            if (!Array.isArray(prevFormC)) {
                                if (typeof prevFormC === "string") {
                                    prevFormC = prevFormC
                                        .split(/[,\n]+/)
                                        .map((s) => s.trim())
                                        .filter(Boolean);
                                } else if (prevFormC) {
                                    prevFormC = [prevFormC];
                                } else {
                                    prevFormC = [];
                                }
                            }
                            uploads.financials = [...prevFormC, ...paths];
                            setLegalUploads((prev) => ({
                                ...prev,
                                financials: uploads.financials,
                            }));
                            setLegalFiles((prev) => ({ ...prev, financials: [] }));
                        } catch (err) {
                            console.error(
                                "Legal doc upload error:",
                                err.response?.data || err.message,
                            );
                            notifyUser(null, "error", "Failed to upload financials.", "toast", null, null, null, null, null, false, false, "top-center");
                        } finally {
                            setLegalUploading((prev) => ({
                                ...prev,
                                financials: false,
                            }));
                        }
                    }

                    // --- Handle Subscription Agreement (multiple) ---
                    if (
                        Array.isArray(legalFiles.subscription_agreement) &&
                        legalFiles.subscription_agreement.length > 0
                    ) {
                        setLegalUploading((prev) => ({
                            ...prev,
                            subscription_agreement: true,
                        }));
                        const saFormData = new FormData();
                        legalFiles.subscription_agreement.forEach((file) =>
                            saFormData.append("legalDoc", file),
                        );
                        saFormData.append("docType", "subscription_agreement");
                        saFormData.append("user_id", user?.id);
                        try {
                            const res = await axios.post(
                                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${currentId}/upload-legal`,
                                saFormData,
                                {
                                    headers: {
                                        "Content-Type": "multipart/form-data",
                                    },
                                    withCredentials: true,
                                },
                            );
                            const paths = Array.isArray(res.data?.paths)
                                ? res.data.paths.map((p) =>
                                    p.replace(/\\/g, "/"),)
                                : [];
                            let prevSA = uploads.subscription_agreement;
                            if (!Array.isArray(prevSA)) {
                                if (typeof prevSA === "string") {
                                    prevSA = prevSA
                                        .split(/[,\n]+/)
                                        .map((s) => s.trim())
                                        .filter(Boolean);
                                } else if (prevSA) {
                                    prevSA = [prevSA];
                                } else {
                                    prevSA = [];
                                }
                            }
                            uploads.subscription_agreement = [...prevSA, ...paths];
                            setLegalUploads((prev) => ({
                                ...prev,
                                subscription_agreement: uploads.subscription_agreement,
                            }));
                            setLegalFiles((prev) => ({ ...prev, subscription_agreement: [] }));
                        } catch (err) {
                            console.error(
                                "Legal doc upload error:",
                                err.response?.data || err.message,
                            );
                            notifyUser(null, "error", "Failed to upload Subscription Agreement.", "toast", null, null, null, null, null, false, false, "top-center");
                        } finally {
                            setLegalUploading((prev) => ({
                                ...prev,
                                subscription_agreement: false,
                            }));
                        }
                    }

                    // --- Handle legacy single uploads ---
                    for (const docType of ["investor_terms"]) {
                        const file = legalFiles[docType];
                        if (file) {
                            setLegalUploading((prev) => ({
                                ...prev,
                                [docType]: true,
                            }));
                            const formData = new FormData();
                            formData.append("legalDoc", file);
                            formData.append("docType", docType);
                            formData.append("user_id", user?.id);
                            try {
                                const res = await axios.post(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${currentId}/upload-legal`,
                                    formData,
                                    {
                                        headers: {
                                            "Content-Type":
                                                "multipart/form-data",
                                        },
                                        withCredentials: true,
                                    },
                                );
                                const filePath = res.data?.path
                                    ? res.data.path.replace(/\\/g, "/")
                                    : "";
                                uploads[docType] = filePath;
                                setLegalUploads((prev) => ({
                                    ...prev,
                                    [docType]: filePath,
                                }));
                                setLegalFiles((prev) => ({
                                    ...prev,
                                    [docType]: null,
                                }));
                            } catch (err) {
                                console.error(
                                    "Legal doc upload error:",
                                    err.response?.data || err.message,
                                );
                                notifyUser(null, "error", `Failed to upload ${docType.replace(
                                        "_",
                                        " ",
                                    )}.`, "toast", null, null, null, null, null, false, false, "top-center");
                            } finally {
                                setLegalUploading((prev) => ({
                                    ...prev,
                                    [docType]: false,
                                }));
                            }
                        }
                    }

                    // Defensive: ensure financials is always an array
                    if (uploads.financials && !Array.isArray(uploads.financials)) {
                        if (typeof uploads.financials === "string") {
                            uploads.financials = uploads.financials
                                .split(/[,\n]+/)
                                .map((s) => s.trim())
                                .filter(Boolean);
                        } else if (uploads.financials) {
                            uploads.financials = [uploads.financials];
                        } else {
                            uploads.financials = [];
                        }
                    }
                    uploads.risks = (uploads.risks || []).map((risk) => risk.trim()).filter(Boolean).slice(0, 10);
                    payload = {
                        ...payload,
                        legal_uploads: uploads,
                        goalHit: goalHit,
                        campaign_start_date: campaignStartDate,
                        max_amount: maxAmount,
                        unit_price: unitPrice,
                    };
                    break;
                }
                default:
                    notifyUser(null, "warning", "This section doesn't support save yet.", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
            }
            // Add end_date to all payloads if it exists
            if (campaignEndDate) {
                payload.end_date = campaignEndDate;
            }

            // Actually save the data to the database
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${currentId}`,
                payload,
                { withCredentials: true },
            );
            if (response.data) {
                notifyUser(null, "success", "Section saved successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                setSaveProgress(true);
                if (activeTab < 5) {
                    setActiveTab(activeTab + 1);
                }
            }
        } catch (err) {
            // Ensure payload is defined for error logging
            payload = typeof payload !== "undefined" ? payload : null;
            console.error("Save section error:", {
                error: err,
                response: err.response?.data,
                payload,
            });
            notifyUser(null, "error", err.response?.data?.error || "Failed to save section.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Submitting campaign
    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (!validateEdgarFilingUrl()) {
                setLoading(false);
                return;
            }

            const userId = user?.id;
            if (!userId) {
                notifyUser(null, "error", "User not logged in.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            const validAppId = parseInt(applicationId, 10);
            if (isNaN(validAppId)) {
                notifyUser(null, "error", "Missing or invalid application ID.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            // Require background image before submitting
            if (!backgroundImage || backgroundImage.trim() === "") {
                notifyUser(null, "error", "Background image is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }

            // Required NC fields
            if (!goalHit || goalHit === "") {
                notifyUser(null, "error", "targetAmount is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!minAmount || minAmount === "") {
                notifyUser(null, "error", "minAmount is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!maxAmount || maxAmount === "") {
                notifyUser(null, "error", "maxAmount is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!validateGoalHitAgainstMaxAmount()) {
                setLoading(false);
                return;
            }
            if (!unitPrice || unitPrice === "") {
                notifyUser(null, "error", "unitPrice is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!campaignStartDate || campaignStartDate === "") {
                notifyUser(null, "error", "startDate is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!campaignEndDate || campaignEndDate === "") {
                notifyUser(null, "error", "endDate is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }
            if (!investorsInfo.trim()) {
                notifyUser(null, "error", "Lead Investor is required.", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
                return;
            }

            // Upload any staged legal files that weren't saved via Save Progress
            let finalLegalUploads = { ...legalUploads };
            finalLegalUploads.edgar_filing_url = String(
                finalLegalUploads.edgar_filing_url || "",
            ).trim();
            if (campaignId) {
                for (const docType of ["subscription_agreement", "financials"]) {
                    if (Array.isArray(legalFiles[docType]) && legalFiles[docType].length > 0) {
                        const fd = new FormData();
                        legalFiles[docType].forEach((f) => fd.append("legalDoc", f));
                        fd.append("docType", docType);
                        fd.append("user_id", user.id);
                        try {
                            const uploadRes = await axios.post(
                                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-legal`,
                                fd,
                                { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true },
                            );
                            const paths = Array.isArray(uploadRes.data?.paths)
                                ? uploadRes.data.paths.map((p) => p.replace(/\\/g, "/"))
                                : [];
                            const prev = Array.isArray(finalLegalUploads[docType])
                                ? finalLegalUploads[docType]
                                : [];
                            finalLegalUploads[docType] = [...prev, ...paths];
                            setLegalUploads((s) => ({ ...s, [docType]: finalLegalUploads[docType] }));
                            setLegalFiles((s) => ({ ...s, [docType]: [] }));
                        } catch (uploadErr) {
                            notifyUser(null, "error", `Failed to upload ${docType === "subscription_agreement" ? "Subscription Agreement" : "Financials"}. Please try again.`, "toast", null, null, null, null, null, false, false, "top-center");
                            setLoading(false);
                            return;
                        }
                    }
                }
            }

            finalLegalUploads.risks = (finalLegalUploads.risks || [])
                .map((risk) => risk.trim())
                .filter(Boolean)
                .slice(0, 10);
            finalLegalUploads.investor_perks = normalizeInvestorPerks(finalLegalUploads.investor_perks)
                .filter((perk) => perk.amount.trim() || perk.description.trim())
                .slice(0, 5);

            // Build payload for submission
            const payload = {
                application_id: validAppId,
                campaign_name: campaignName,
                campaign_description: campaignDescription,
                overview_pitch: overviewPitch.trim(),
                call_to_action: callToAction,
                pitch_problem: pitchProblem,
                pitch_solution: pitchSolution,
                pitch_slides: pitchSlides,
                founders: founders,
                updates: updates,
                facebook_link: facebookLink,
                linkedin_link: linkedinLink,
                twitter_link: twitterLink,
                tiktok_link: tiktokLink,
                youtube_link: youtubeLink,
                instagram_link: instagramLink,
                company_name: companyName,
                company_city: companyCity,
                company_state: companyState,
                investors_info: investorsInfo,
                end_date: campaignEndDate,
                campaign_start_date: campaignStartDate,
                max_amount: maxAmount,
                unit_price: unitPrice,
                investment_min: minAmount,
                minimum_raised: minimumRaised,
                submitted: true,
                goalHit: goalHit,

            };
            payload.legal_uploads = finalLegalUploads;
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns`,
                payload,
                { withCredentials: true },
            );
            console.log("Submitted:", response.data);
            notifyUser(null, "success", "Campaign submitted successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            setSubmitted(true);
            setCampaignId(response.data.campaignId);
            setStatus("pending");
        } catch (err) {
            console.error("Submit error:", err.response?.data || err.message);
            notifyUser(null, "error", "Failed to submit campaign.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    // submitting campaign changes in to campaign_drafts table
    const handleSubmitDraft = async () => {
        try {
            if (!validateEdgarFilingUrl()) return;

            if (!user?.id || !campaignId) {
                notifyUser(null, "error", "Missing user ID or campaign ID.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            if (!validateGoalHitAgainstMaxAmount()) {
                return;
            }
            if (!investorsInfo.trim()) {
                notifyUser(null, "error", "Lead Investor is required.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }
            // LOG: Start draft submit

            // Upload any staged legal files that weren't saved via Save Progress
            let finalDraftLegalUploads = { ...legalUploads };
            finalDraftLegalUploads.edgar_filing_url = String(
                finalDraftLegalUploads.edgar_filing_url || "",
            ).trim();
            for (const docType of ["subscription_agreement", "financials"]) {
                if (Array.isArray(legalFiles[docType]) && legalFiles[docType].length > 0) {
                    const fd = new FormData();
                    legalFiles[docType].forEach((f) => fd.append("legalDoc", f));
                    fd.append("docType", docType);
                    fd.append("user_id", user.id);
                    try {
                        const uploadRes = await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-legal`,
                            fd,
                            { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true },
                        );
                        const paths = Array.isArray(uploadRes.data?.paths)
                            ? uploadRes.data.paths.map((p) => p.replace(/\\/g, "/"))
                            : [];
                        const prev = Array.isArray(finalDraftLegalUploads[docType])
                            ? finalDraftLegalUploads[docType]
                            : [];
                        finalDraftLegalUploads[docType] = [...prev, ...paths];
                        setLegalUploads((s) => ({ ...s, [docType]: finalDraftLegalUploads[docType] }));
                        setLegalFiles((s) => ({ ...s, [docType]: [] }));
                    } catch (uploadErr) {
                        notifyUser(null, "error", `Failed to upload ${docType === "subscription_agreement" ? "Subscription Agreement" : "Financials"}. Please try again.`, "toast", null, null, null, null, null, false, false, "top-center");
                        return;
                    }
                }
            }

            finalDraftLegalUploads.risks = (finalDraftLegalUploads.risks || [])
                .map((risk) => risk.trim())
                .filter(Boolean)
                .slice(0, 10);
            finalDraftLegalUploads.investor_perks = normalizeInvestorPerks(finalDraftLegalUploads.investor_perks)
                .filter((perk) => perk.amount.trim() || perk.description.trim())
                .slice(0, 5);

            const payload = {
                user_id: user.id,
                campaign_name: campaignName,
                campaign_description: campaignDescription,
                company_name: companyName,
                company_city: companyCity,
                company_state: companyState,
                investors_info: investorsInfo,
                overview_pitch: overviewPitch,
                call_to_action: callToAction,
                pitch_problem: pitchProblem,
                pitch_solution: pitchSolution,
                pitch_slides: pitchSlides,
                founders: founders,
                updates: updates,
                facebook_link: facebookLink,
                linkedin_link: linkedinLink,
                twitter_link: twitterLink,
                tiktok_link: tiktokLink,
                youtube_link: youtubeLink,
                instagram_link: instagramLink,
                investment_amount: investmentAmount,
                campaign_background_image: backgroundImage,
                submitted: true,
                fund_raising_goal: goalHit,
                end_date: campaignEndDate,
                campaign_start_date: campaignStartDate,
                max_amount: maxAmount,
                unit_price: unitPrice,
                investment_minimum: minAmount,
                investment_min: minAmount,
                minimum_raised: minimumRaised,

            };
            payload.legal_uploads = finalDraftLegalUploads;

            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/save-draft`,
                payload,
                { withCredentials: true },
            );

            if (res.data?.message) {
                notifyUser(null, "success", "Changes submitted for review.", "toast", null, null, null, null, null, false, false, "top-center");
                setDraftSubmitted(true);
                if (status !== "disabled") {
                    setStatus("pending");
                }
                setIsEditing(false);
            }
        } catch (err) {
            // Ensure payload is defined for error logging
            let payload = typeof payload !== "undefined" ? payload : null;
            console.error("Draft submit failed:", err, "payload:", payload);
            notifyUser(null, "error", "Failed to submit changes.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className="min-h-screen p-4 flex flex-col mt-20">
            {/* ...existing code... */}
            {/* Main Section: Left (drag+tabs), Right (form+investment) */}
            <div className="flex flex-col md:flex-row gap-4">
                {/* LEFT COLUMN */}
                <div className="w-full md:w-2/3 flex flex-col">
                    {/* Drag & Drop Upload */}
                    <div className="mb-4">
                        <div
                            className={`h-64 sm:h-80 md:h-96 lg:h-[400px] xl:h-[450px] 2xl:h-[500px] border border-gray-400 flex items-center justify-center text-center text-sm text-black cursor-pointer transition-colors duration-200 rounded-lg overflow-hidden ${isDragActive ? "bg-blue-100 border-blue-400" : ""
                                }`}
                            onClick={handleBoxClick}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            {backgroundImage ? (
                                <img
                                    key={backgroundImage}
                                    src={backgroundImage}
                                    alt="Campaign Background"
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <span>
                                    {isDragActive
                                        ? "Drop image here..."
                                        : "Click or Drag & Drop to upload background image"}
                                </span>
                            )}
                        </div>

                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                        setCroppingImage(reader.result);
                                        setShowCropper(true);
                                    };
                                    reader.readAsDataURL(file);
                                }
                            }}
                            className="hidden"
                            disabled={submitted && !isEditing}
                        />
                    </div>

                    {/* Tabs Section */}
                    <div className="w-full mt-3 mb-5">
                        <div className="flex w-full gap-2">
                            {[
                                { label: "About", icon: "fa-info-circle" },
                                { label: "Pitch", icon: "fa-bullhorn" },
                                { label: "Team", icon: "fa-users" },
                                { label: "Updates", icon: "fa-bell" },
                                { label: "Socials", icon: "fa-share-alt" },
                                { label: "Legal & Financials", icon: "fa-file-contract" },
                                // Chat tab temporarily hidden but functionality preserved
                                // ...(status === 'approved' ? [{ label: "Chat", icon: "fa-comments" }] : [])
                            ].map(({ label, icon }, idx) => (
                                <button
                                    key={label}
                                    onClick={() => setActiveTab(idx)}
                                    className={`flex-1 flex items-center justify-center gap-2 px-2 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap border ${
                                        activeTab === idx
                                            ? "bg-navy text-white border-navy shadow-sm"
                                            : "bg-white text-[#6b7a8f] border-[#e3eaf3] hover:border-navy hover:text-navy"
                                    }`}
                                >
                                    <i className={`fas ${icon} text-xs`}></i>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* About Section */}
                    {activeTab === 0 && (
                        <div className="space-y-4">
                            <h2 className="text-[#8B1C2D] font-bold text-xl">
                                ABOUT
                            </h2>
                            {submitted && !isEditing ? (
                                /* ── Read-only view ── */
                                <div className="border border-[#e3eaf3] rounded-2xl bg-white px-6 py-4 space-y-4">
                                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                                        {[
                                            { label: "Company Name", value: companyName },
                                            { label: "Company City", value: companyCity },
                                            { label: "Company State", value: companyState },
                                            { label: "Lead Investor", value: investorsInfo },
                                        ].map(({ label, value }) => (
                                            <div key={label}>
                                                <dt className="text-[10px] font-semibold text-[#6b7a8f] uppercase tracking-wide mb-0.5">{label}</dt>
                                                <dd className="text-[#23304a] text-sm font-medium">{value || <span className="text-gray-300 italic">—</span>}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                    <div>
                                        <p className="text-[10px] font-semibold text-[#6b7a8f] uppercase tracking-wide mb-1">Overview of Pitch</p>
                                        <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{overviewPitch || <span className="text-gray-300 italic">—</span>}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold text-[#6b7a8f] uppercase tracking-wide mb-1">Call to Action</p>
                                        <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{callToAction || <span className="text-gray-300 italic">—</span>}</p>
                                    </div>
                                </div>
                            ) : (
                                /* ── Edit view ── */
                                <div className="space-y-4">
                                    <p className="italic text-sm mb-4">
                                        ENTER DATA AS YOU WANT TO SEE IT ON YOUR CAMPAIGN PAGE
                                    </p>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Company Name*</label>
                                        <input
                                            type="text"
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Company City*</label>
                                        <input
                                            type="text"
                                            value={companyCity}
                                            onChange={(e) => setCompanyCity(e.target.value)}
                                            className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Company State*</label>
                                        <input
                                            type="text"
                                            value={companyState}
                                            onChange={(e) => setCompanyState(e.target.value)}
                                            className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Lead Investor *</label>
                                        <input
                                            type="text"
                                            value={investorsInfo}
                                            onChange={(e) => setInvestorsInfo(e.target.value)}
                                            required
                                            className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Overview of Pitch: *</label>
                                        <div className="border border-navy rounded-2xl p-3">
                                            <div className="text-sm text-gray-500 mb-2">
                                                Text counter length – {maxLongFormLength - overviewPitch.length}
                                            </div>
                                            <textarea
                                                maxLength={maxLongFormLength}
                                                rows={8}
                                                value={overviewPitch}
                                                onChange={(e) => setOverviewPitch(e.target.value)}
                                                className="w-full text-sm text-navy focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-navy font-semibold mb-1">Call to Action: *</label>
                                        <div className="border border-navy rounded-2xl p-3">
                                            <div className="text-sm text-gray-500 mb-2">
                                                Text counter length – {maxDescriptionLength - callToAction.length}
                                            </div>
                                            <textarea
                                                maxLength={maxDescriptionLength}
                                                rows={4}
                                                value={callToAction}
                                                onChange={(e) => setCallToAction(e.target.value)}
                                                className="w-full text-sm text-navy focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* {status !== 'pending' && status !== 'approved' && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSaveSection}
                    className="aqi-primary-action rounded-2xl bg-navy px-6 py-2 text-sm font-semibold text-white"
                  >
                    Save
                  </button>
                </div>
              )} */}
                        </div>
                    )}
                    {/* Pitch Section */}
                    {activeTab === 1 && (
                        <div className="space-y-6">
                            <h2 className="text-[#8B1C2D] font-bold text-xl">
                                PITCH
                            </h2>

                            {submitted && !isEditing ? (
                                /* ── Read-only view ── */
                                <div className="space-y-6">
                                    {/* Problem & Solution */}
                                    <div className="border border-[#e3eaf3] rounded-2xl bg-white divide-y divide-[#e3eaf3]">
                                        <div className="px-6 py-4">
                                            <span className="block text-xs font-semibold text-[#6b7a8f] uppercase tracking-wide mb-2">Problem</span>
                                            <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{pitchProblem || <span className="text-gray-300 italic">—</span>}</p>
                                        </div>
                                        <div className="px-6 py-4">
                                            <span className="block text-xs font-semibold text-[#6b7a8f] uppercase tracking-wide mb-2">Solution</span>
                                            <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{pitchSolution || <span className="text-gray-300 italic">—</span>}</p>
                                        </div>
                                    </div>

                                    {/* Slides */}
                                    {pitchSlides.map((slide, idx) => {
                                        const slideUrl = slide.file
                                            ? (slide.file.startsWith("http") ? slide.file : `${process.env.REACT_APP_BACKEND_URL}/uploads/${slide.file}`)
                                            : null;
                                        return (
                                            <div key={idx} className="border border-[#e3eaf3] rounded-2xl bg-white p-6">
                                                <p className="text-xs font-semibold text-[#6b7a8f] uppercase tracking-wide mb-4">Pitch Slide {idx + 1}</p>
                                                <div className="flex gap-6 items-start">
                                                    {/* Thumbnail */}
                                                    {slideUrl ? (
                                                        slideUrl.match(/\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i) ? (
                                                            <img
                                                                src={slideUrl}
                                                                alt={`Pitch Slide ${idx + 1}`}
                                                                className="w-28 h-28 object-cover rounded-xl border border-navy/20 shrink-0 cursor-pointer"
                                                                onClick={() => { setActiveImage(slideUrl); setModalOpen(true); }}
                                                            />
                                                        ) : slideUrl.match(/\.pdf(\?.*)?$/i) ? (
                                                            <div
                                                                className="w-28 h-28 rounded-xl border border-navy/20 bg-white shrink-0 flex flex-col items-center justify-center gap-1 cursor-pointer"
                                                                onClick={() => { setActivePdf(slideUrl); setModalOpen(true); }}
                                                            >
                                                                <i className="fas fa-file-pdf text-4xl text-navy"></i>
                                                                <span className="text-xs text-navy font-medium">PDF</span>
                                                            </div>
                                                        ) : null
                                                    ) : (
                                                        <div className="w-28 h-28 rounded-xl border border-[#e3eaf3] bg-[#F5F7FA] shrink-0 flex items-center justify-center text-gray-300 text-xs">No file</div>
                                                    )}
                                                    {/* Overview */}
                                                    <div className="flex-1">
                                                        <span className="block text-xs font-semibold text-[#6b7a8f] uppercase tracking-wide mb-2">Overview</span>
                                                        <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{slide.overview || <span className="text-gray-300 italic">—</span>}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* ── Edit view ── */
                                <div className="space-y-6">
                                    {/* Problem */}
                                    <div>
                                        <label className="block text-navy font-bold mb-1">Problem : *</label>
                                        <div className="border border-navy rounded-2xl p-3 mb-2">
                                            <div className="text-sm text-gray-500 mb-2">
                                                Text counter length – {maxLongFormLength - pitchProblem.length}
                                            </div>
                                            <textarea
                                                maxLength={maxLongFormLength}
                                                rows={8}
                                                value={pitchProblem}
                                                onChange={(e) => setPitchProblem(e.target.value)}
                                                className="w-full text-sm text-navy focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    {/* Solution */}
                                    <div>
                                        <label className="block text-navy font-bold mb-1">Solution : *</label>
                                        <div className="border border-navy rounded-2xl p-3 mb-2">
                                            <div className="text-sm text-gray-500 mb-2">
                                                Text counter length – {maxLongFormLength - pitchSolution.length}
                                            </div>
                                            <textarea
                                                maxLength={maxLongFormLength}
                                                rows={8}
                                                value={pitchSolution}
                                                onChange={(e) => setPitchSolution(e.target.value)}
                                                className="w-full text-sm text-navy focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    {/* Slides */}
                                    {pitchSlides.map((slide, idx) => (
                                        <div key={idx} className="mb-10">
                                            <div className="flex flex-col items-center my-4">
                                                <label className="text-navy text-lg font-semibold mb-2">
                                                    Upload Pitch Slide {idx + 1}
                                                </label>
                                                <label className="cursor-pointer flex flex-col items-center text-navy">
                                                    <input
                                                        type="file"
                                                        accept="application/pdf,image/*"
                                                        onChange={(e) => uploadPitchSlide(idx, e.target.files[0])}
                                                        className="hidden"
                                                    />
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-1" viewBox="0 0 20 20" fill="var(--navy)">
                                                        <path fillRule="evenodd" d="M3 3a2 2 0 012-2h10a2 2 0 012 2v2h-2V3H5v14h10v-2h2v2a2 2 0 01-2 2H5a2 2 0 01-2-2V3zm7 8V5h2v6h3l-4 4-4-4h3z" clipRule="evenodd" />
                                                    </svg>
                                                </label>
                                                {slide.file &&
                                                    (slide.file.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                                        <img
                                                            src={slide.file}
                                                            alt={`Pitch Slide ${idx + 1} Preview`}
                                                            className="w-32 h-32 object-cover rounded-lg border border-navy mb-2 cursor-pointer"
                                                            onClick={() => { setActiveImage(slide.file); setModalOpen(true); }}
                                                        />
                                                    ) : slide.file.match(/\.pdf$/i) ? (
                                                        <div
                                                            className="w-32 h-32 rounded-lg border border-navy mb-2 bg-white cursor-pointer flex flex-col items-center justify-center"
                                                            onClick={() => { setActivePdf(slide.file); setModalOpen(true); }}
                                                        >
                                                            <span className="text-6xl text-navy mb-2"><i className="fas fa-file-pdf"></i></span>
                                                            <span className="text-xs text-navy">PDF Preview</span>
                                                        </div>
                                                    ) : (
                                                        <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded-lg border border-navy mb-2 text-navy">No preview</div>
                                                    ))}
                                            </div>
                                            <label className="block text-navy font-bold mb-1">
                                                Slide Overview: *
                                            </label>
                                            <div className="border border-navy rounded-2xl p-3 mb-2">
                                                <div className="text-sm text-gray-500 mb-2">
                                                    Text counter length – {maxLongFormLength - slide.overview.length}
                                                </div>
                                                <textarea
                                                    maxLength={maxLongFormLength}
                                                    rows={8}
                                                    value={slide.overview}
                                                    onChange={(e) => handleSlideChange(idx, e.target.value)}
                                                    className="w-full text-sm text-navy focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {/* +/- Controls */}
                                    <div className="flex items-center justify-end gap-4">
                                        <button onClick={removeSlide} disabled={pitchSlides.length === 1} className="text-3xl text-navy font-bold px-2">-</button>
                                        <span className="text-navy text-lg">up to 8 total slides</span>
                                        <button onClick={addSlide} disabled={pitchSlides.length === maxSlides} className="text-3xl text-navy font-bold px-2">+</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Team Section */}
                    {activeTab === 2 && (
                        <div className="space-y-8">
                            <h2 className="text-[#8B1C2D] font-bold text-xl">
                                TEAM
                            </h2>

                            {submitted && !isEditing ? (
                                /* ── Read-only view ── */
                                <div className="space-y-4">
                                    {founders.map((founder, idx) => {
                                        const headshotUrl = founder.file
                                            ? (founder.file.startsWith("http") ? founder.file : `${process.env.REACT_APP_BACKEND_URL}/uploads/${founder.file}`)
                                            : null;
                                        return (
                                            <div key={idx} className="border border-[#e3eaf3] rounded-2xl bg-white p-6 flex gap-6 items-start">
                                                {headshotUrl ? (
                                                    <img
                                                        src={headshotUrl}
                                                        alt={`Founder ${idx + 1}`}
                                                        className="w-20 h-20 rounded-full object-cover border border-navy/20 shrink-0 cursor-pointer"
                                                        onClick={() => { setActiveImage(headshotUrl); setModalOpen(true); }}
                                                    />
                                                ) : (
                                                    <div className="w-20 h-20 rounded-full bg-[#f0f6fb] border border-[#e3eaf3] shrink-0 flex items-center justify-center">
                                                        <i className="fas fa-user text-2xl text-[#c5d3e0]"></i>
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[#23304a] font-semibold text-base mb-2">{founder.name || <span className="text-gray-300 italic">Unnamed Founder</span>}</p>
                                                    {founder.linkedin && (
                                                        <a
                                                            href={founder.linkedin.startsWith("http") ? founder.linkedin : `https://${founder.linkedin}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-[#0077b5] hover:underline"
                                                        >
                                                            <i className="fab fa-linkedin"></i>
                                                            LinkedIn profile
                                                        </a>
                                                    )}
                                                    <span className="block text-xs font-semibold text-[#6b7a8f] uppercase tracking-wide mb-2">About</span>
                                                    <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{founder.about || <span className="text-gray-300 italic">—</span>}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* ── Edit view ── */
                                <div className="space-y-8">
                                    {founders.map((founder, idx) => (
                                        <div key={idx} className="space-y-4">
                                            <div>
                                                <label className="block text-navy font-bold mb-1">
                                                    Founder Full Name:*
                                                </label>
                                                <input
                                                    type="text"
                                                    value={founder.name}
                                                    onChange={(e) =>
                                                        handleFounderChange(
                                                            idx,
                                                            "name",
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                                    placeholder="John Doe"
                                                />
                                            </div>
                                            <div className="flex flex-col items-center my-2">
                                                <label className="text-navy text-base mb-2">
                                                    Upload Headshot
                                                </label>
                                                <label className="cursor-pointer flex flex-col items-center text-navy">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(e) =>
                                                            uploadFounderHeadshot(
                                                                idx,
                                                                e.target.files[0],
                                                            )
                                                        }
                                                    />
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-10 w-10 mb-1"
                                                        viewBox="0 0 20 20"
                                                        fill="var(--navy)"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M3 3a2 2 0 012-2h10a2 2 0 012 2v2h-2V3H5v14h10v-2h2v2a2 2 0 01-2 2H5a2 2 0 01-2-2V3zm7 8V5h2v6h3l-4 4-4-4h3z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </label>

                                                {founder.file && (
                                                    <img
                                                        src={founder.file}
                                                        alt={`Founder ${idx + 1} Headshot`}
                                                        className="h-32 w-32 object-cover rounded-full border border-navy cursor-pointer mt-1"
                                                        onClick={() => {
                                                            setActiveImage(founder.file);
                                                            setModalOpen(true);
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-navy font-bold mb-1">
                                                    LinkedIn Profile
                                                </label>
                                                <input
                                                    type="url"
                                                    value={founder.linkedin || ""}
                                                    onChange={(e) =>
                                                        handleFounderChange(
                                                            idx,
                                                            "linkedin",
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                                    placeholder="https://www.linkedin.com/in/name"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-navy font-bold mb-1">
                                                    About the Founder : *
                                                </label>
                                                <div className="border border-navy rounded-2xl p-3">
                                                    <div className="text-sm text-gray-500 mb-2">
                                                        Text counter length –{" "}
                                                        {maxLongFormLength -
                                                            founder.about.length}
                                                    </div>
                                                    <textarea
                                                        maxLength={maxLongFormLength}
                                                        rows={8}
                                                        value={founder.about}
                                                        onChange={(e) =>
                                                            handleFounderChange(
                                                                idx,
                                                                "about",
                                                                e.target.value,
                                                            )
                                                        }

                                                        className="w-full text-sm text-navy focus:outline-none"
                                                        placeholder="Share this team member’s background, expertise, responsibilities, and relevant experience."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {/* +/- Controls */}
                                    <div className="flex items-center justify-end gap-4 mt-6">
                                        <button
                                            onClick={removeFounder}
                                            disabled={founders.length === 1}
                                            className="text-3xl text-navy font-bold px-2"
                                        >
                                            -
                                        </button>

                                        <span className="text-navy text-lg">
                                            up to {maxFounders} total founders
                                        </span>

                                        <button
                                            onClick={addFounder}
                                            disabled={founders.length === maxFounders}
                                            className="text-3xl text-navy font-bold px-2"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Updates Section */}
                    {activeTab === 3 && (
                        <div className="space-y-6">
                            <h2 className="text-[#8B1C2D] font-bold text-xl">
                                UPDATES
                            </h2>

                            {(!submitted || isEditing) && (
                                <>
                                    <p className="text-sm text-gray-600 italic mb-3">
                                        Publish a detailed campaign update with an optional title, link, picture, video, or document.
                                    </p>
                                    <form
                                        className="mb-4 space-y-3 rounded-2xl border border-[#e3eaf3] bg-white p-4"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handlePostUpdate();
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={newUpdateTitle}
                                            onChange={(e) => setNewUpdateTitle(e.target.value)}
                                            disabled={submitted && !isEditing}
                                            placeholder="Update title"
                                            maxLength={200}
                                            className="w-full rounded-xl border border-navy p-3 text-sm font-semibold focus:outline-none"
                                        />
                                        <input
                                            type="url"
                                            value={newUpdateUrl}
                                            onChange={(e) => setNewUpdateUrl(e.target.value)}
                                            disabled={submitted && !isEditing}
                                            placeholder="Related link (optional)"
                                            className="w-full rounded-xl border border-navy p-3 text-sm focus:outline-none"
                                        />
                                        <textarea
                                            value={newUpdate}
                                            onChange={(e) => setNewUpdate(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Escape") {
                                                    setNewUpdate("");
                                                }
                                                if (
                                                    e.key === "Enter" &&
                                                    (e.ctrlKey || e.metaKey)
                                                ) {
                                                    e.preventDefault();
                                                    handlePostUpdate();
                                                }
                                            }}
                                            disabled={submitted && !isEditing}
                                            placeholder="Write a detailed update..."
                                            maxLength={maxLongFormLength}
                                            className="w-full rounded-xl border border-navy p-3 text-sm leading-relaxed focus:outline-none"
                                            rows={10}
                                        />
                                        {/* Media preview */}
                                        {updateMedia && (
                                            <div className="relative mb-2 inline-block">
                                                {getMediaType(updateMedia) === "image" && (
                                                    <img
                                                        src={updateMedia}
                                                        alt="Update preview"
                                                        className="h-32 object-contain rounded-lg border border-navy"
                                                        onClick={() => {
                                                            setActiveImage(updateMedia);
                                                            setModalOpen(true);
                                                        }}
                                                    />
                                                )}
                                                {getMediaType(updateMedia) === "video" && (
                                                    <video
                                                        src={updateMedia}
                                                        controls
                                                        className="h-32 object-contain rounded-lg border border-navy"
                                                    />
                                                )}
                                                {getMediaType(updateMedia) ===
                                                    "document" && (
                                                        <div
                                                            className="h-32 w-32 flex flex-col items-center justify-center bg-gray-100 rounded-lg border border-navy cursor-pointer"
                                                            onClick={() => {
                                                                setActivePdf(updateMedia);
                                                                setModalOpen(true);
                                                            }}
                                                        >
                                                            <span className="text-4xl text-navy">
                                                                <i className="fas fa-file-pdf"></i>
                                                            </span>
                                                            <span className="text-xs text-navy mt-2">
                                                                PDF Document
                                                            </span>
                                                        </div>
                                                    )}
                                                <button
                                                    type="button"
                                                    className="absolute top-0 right-0 bg-white rounded-full text-red-600 p-1 shadow-md"
                                                    onClick={handleCancelUpdateMedia}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-5 w-5"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <input
                                                    type="file"
                                                    ref={updateFileInputRef}
                                                    onChange={(e) => {
                                                        if (
                                                            e.target.files &&
                                                            e.target.files[0]
                                                        ) {
                                                            uploadUpdateMedia(
                                                                e.target.files[0],
                                                            );
                                                        }
                                                    }}
                                                    accept="image/*,video/*,application/pdf"
                                                    className="hidden"
                                                    disabled={
                                                        (submitted && !isEditing) ||
                                                        updateMediaUploading
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleUpdateMediaSelect}
                                                    disabled={
                                                        (submitted && !isEditing) ||
                                                        updateMediaUploading
                                                    }
                                                    className="mr-2 bg-gray-200 hover:bg-gray-300 text-navy rounded-full p-2"
                                                    title="Add photo, video, or document"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-5 w-5"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </button>
                                                {updateMediaUploading && (
                                                    <span className="text-xs text-gray-500">
                                                        Uploading...
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={
                                                    updateMediaUploading ||
                                                    (!newUpdateTitle.trim() &&
                                                        !newUpdate.trim() &&
                                                        !newUpdateUrl.trim() &&
                                                        !updateMedia)
                                                }
                                                className="rounded-xl bg-navy px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#003A61] disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Add Update
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                            <div>
                                {updates.length === 0 ? (
                                    <p className="italic text-navy text-lg">
                                        ANY UPDATES TO THE ACCOUNT WILL BE LISTED
                                        HERE
                                    </p>
                                ) : (
                                    <ul className="space-y-4">
                                        {updates.map((update, idx) => {
                                            const isUpdateObject = typeof update === "object" && update !== null;
                                            const updateTitle = isUpdateObject ? update.title : "";
                                            const updateText = isUpdateObject
                                                ? update.description || update.text
                                                : typeof update === "string"
                                                    ? update
                                                    : "";
                                            const updateUrl = isUpdateObject ? update.url : "";
                                            const updateMediaUrl = isUpdateObject && update.media
                                                ? normalizeAssetUrl(update.media)
                                                : "";

                                            return (
                                            <li
                                                key={idx}
                                                className={`border rounded-2xl p-5 text-sm relative ${editingUpdateIdx === idx
                                                        ? "border-navy bg-gray-100"
                                                        : "border-[#e3eaf3] bg-white"
                                                    }`}
                                            >
                                                {/* Delete and edit buttons for each update - hidden when submitted and not editing,
                            or when this particular update is being edited */}
                                                {(!submitted || isEditing) &&
                                                    editingUpdateIdx !== idx && (
                                                        <div className="absolute top-2 right-2 flex space-x-1">
                                                            {/* Edit button */}
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleEditUpdate(
                                                                        idx,
                                                                    )
                                                                }
                                                                className="text-navy p-1 hover:bg-gray-200 rounded-full"
                                                                title="Edit this update"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-5 w-5"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                </svg>
                                                            </button>

                                                            {/* Delete button */}
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleDeleteUpdate(
                                                                        idx,
                                                                    )
                                                                }
                                                                className="text-red-600 p-1 hover:bg-gray-200 rounded-full"
                                                                title="Delete this update"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-5 w-5"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    )}

                                                {updateMediaUrl && (
                                                    <div className="mb-3">
                                                        {getMediaType(
                                                            updateMediaUrl,
                                                        ) === "image" && (
                                                                <img
                                                                    src={updateMediaUrl}
                                                                    alt={`Update ${idx + 1
                                                                        }`}
                                                                    className="max-h-60 rounded-lg cursor-pointer"
                                                                    onClick={() => {
                                                                        setActiveImage(
                                                                            updateMediaUrl,
                                                                        );
                                                                        setModalOpen(
                                                                            true,
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                        {getMediaType(
                                                            updateMediaUrl,
                                                        ) === "video" && (
                                                                <video
                                                                    src={updateMediaUrl}
                                                                    controls
                                                                    className="max-h-60 rounded-lg w-full"
                                                                />
                                                            )}
                                                        {getMediaType(
                                                            updateMediaUrl,
                                                        ) === "document" && (
                                                                <div
                                                                    className="h-40 flex flex-col items-center justify-center bg-gray-100 rounded-lg border border-navy cursor-pointer"
                                                                    onClick={() => {
                                                                        setActivePdf(
                                                                            updateMediaUrl,
                                                                        );
                                                                        setModalOpen(
                                                                            true,
                                                                        );
                                                                    }}
                                                                >
                                                                    <span className="text-5xl text-navy">
                                                                        <i className="fas fa-file-pdf"></i>
                                                                    </span>
                                                                    <span className="text-sm text-navy mt-2">
                                                                        View PDF
                                                                        Document
                                                                    </span>
                                                                </div>
                                                            )}
                                                    </div>
                                                )}
                                                {updateTitle && editingUpdateIdx !== idx && (
                                                    <p className="text-navy text-base font-semibold leading-snug mb-2 break-words">{updateTitle}</p>
                                                )}
                                                {updateUrl && editingUpdateIdx !== idx && (
                                                    <a
                                                        href={normalizeExternalUrl(updateUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="mb-2 inline-flex w-fit max-w-full items-center gap-2 rounded-full border border-navy/20 bg-[#f0f6fb] px-3 py-1 text-sm font-semibold text-navy transition hover:border-navy/40 hover:bg-[#e3eef8]"
                                                    >
                                                        <i className="fas fa-external-link-alt text-[10px]"></i>
                                                        <span className="truncate">
                                                            {getLinkLabel(updateUrl)}
                                                        </span>
                                                    </a>
                                                )}
                                                {/* Show update text only when not being edited */}
                                                {updateText && editingUpdateIdx !== idx && (
                                                    <p className="text-[#23304a] text-sm leading-relaxed whitespace-pre-wrap">{updateText}</p>
                                                )}

                                                {/* Editing interface when this update is being edited */}
                                                {editingUpdateIdx === idx && (
                                                    <div className="relative mt-2 space-y-2 pt-2">
                                                        <input
                                                            type="text"
                                                            value={editingUpdateTitle}
                                                            onChange={(e) => setEditingUpdateTitle(e.target.value)}
                                                            className="w-full rounded-lg border border-navy p-2 text-sm font-semibold focus:outline-none"
                                                            placeholder="Update title"
                                                            maxLength={200}
                                                        />
                                                        <input
                                                            type="url"
                                                            value={editingUpdateUrl}
                                                            onChange={(e) => setEditingUpdateUrl(e.target.value)}
                                                            className="w-full rounded-lg border border-navy p-2 text-sm focus:outline-none"
                                                            placeholder="Related link (optional)"
                                                        />
                                                        <textarea
                                                            value={
                                                                editingUpdateText
                                                            }
                                                            onChange={(e) =>
                                                                setEditingUpdateText(
                                                                    e.target.value,
                                                                )
                                                            }
                                                            className="w-full border border-navy rounded-lg p-2 text-sm leading-relaxed focus:outline-none"
                                                            rows={8}
                                                            maxLength={maxLongFormLength}
                                                            autoFocus
                                                        />
                                                        <div className="flex justify-end space-x-2">
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    handleCancelEditUpdate
                                                                }
                                                                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleSaveEditUpdate(
                                                                        idx,
                                                                    )
                                                                }
                                                                className="px-3 py-1 bg-navy hover:bg-[#003a5b] text-white rounded"
                                                                disabled={
                                                                    !editingUpdateTitle.trim() &&
                                                                    !editingUpdateText.trim() &&
                                                                    !editingUpdateUrl.trim() &&
                                                                    !update.media
                                                                }
                                                            >
                                                                Save
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {update.timestamp && (
                                                    <div className="mt-3 text-xs text-[#6b7a8f]">
                                                        {new Date(
                                                            update.timestamp,
                                                        ).toLocaleString()}
                                                    </div>
                                                )}
                                            </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Socials Section */}
                    {activeTab === 4 && (
                        <div className="space-y-6">
                            <h2 className="text-[#8B1C2D] font-bold text-xl mb-4">
                                SOCIAL MEDIA FEED
                            </h2>
                            {submitted && !isEditing ? (
                                campaignLinks.length > 0 ? (
                                    <div className="flex flex-wrap justify-center gap-4">
                                        {campaignLinks.map((link) => (
                                            <a
                                                key={link.label}
                                                href={normalizeExternalUrl(link.url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex h-40 w-full flex-col items-center justify-center rounded-lg border-2 bg-white p-4 shadow transition sm:p-6 md:flex-none ${campaignLinkWidthClass} ${link.className}`}
                                            >
                                                <span className="text-lg sm:text-2xl font-bold mb-2 flex items-center gap-2">
                                                    <i className={link.icon}></i>
                                                    {link.label}
                                                </span>
                                                <span className="text-sm sm:text-base font-semibold underline truncate w-full text-center">
                                                    View on {link.label}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="italic text-navy text-base">
                                        No campaign links provided.
                                    </p>
                                )
                            ) : (
                            <div className="space-y-4">
                                <div className="rounded-xl border border-navy/20 bg-white p-4">
                                    <label className="mb-1 block text-sm font-semibold text-navy">
                                        Business Website
                                    </label>
                                    <input
                                        type="url"
                                        value={websiteLink}
                                        readOnly
                                        placeholder="Add the website in your company profile"
                                        className="w-full rounded-lg border border-navy/30 bg-gray-50 px-3 py-2 text-sm text-navy outline-none"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        This address is managed from your company profile.
                                    </p>
                                </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {/* Facebook */}
                                <div className="flex items-center gap-2 bg-[#3b5998] rounded-lg px-4 py-3">
                                    <span className="text-white text-2xl">
                                        <i className="fab fa-facebook-f"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Facebook Page URL"
                                        value={facebookLink}
                                        onChange={(e) =>
                                            setFacebookLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-[#3b5998] text-white border-none outline-none flex-1 placeholder-white rounded-2xl"
                                    />
                                </div>
                                {/* LinkedIn */}
                                <div className="flex items-center gap-2 bg-[#0077b5] rounded-lg px-4 py-3">
                                    <span className="text-white text-2xl">
                                        <i className="fab fa-linkedin-in"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="LinkedIn post URL"
                                        value={linkedinLink}
                                        onChange={(e) =>
                                            setLinkedinLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-[#0077b5] text-white border-none outline-none flex-1 placeholder-white rounded-2xl"
                                    />
                                </div>
                                {/* Twitter */}
                                <div className="flex items-center gap-2 bg-[#1da1f2] rounded-lg px-4 py-3">
                                    <span className="text-white text-2xl">
                                        <i className="fab fa-twitter"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="X / Twitter profile URL"
                                        value={twitterLink}
                                        onChange={(e) =>
                                            setTwitterLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-[#1da1f2] text-white border-none outline-none flex-1 placeholder-white rounded-2xl"
                                    />
                                </div>
                                {/* TikTok */}
                                <div className="flex items-center gap-2 border border-gray-400 rounded-lg px-4 py-3">
                                    <span className="text-black text-2xl">
                                        <i className="fab fa-tiktok"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="TikTok video URL"
                                        value={tiktokLink}
                                        onChange={(e) =>
                                            setTiktokLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-transparent text-black border-none outline-none flex-1 placeholder-black rounded-2xl"
                                    />
                                </div>
                                {/* YouTube */}
                                <div className="flex items-center gap-2 bg-[#ff0000] rounded-lg px-4 py-3">
                                    <span className="text-white text-2xl">
                                        <i className="fab fa-youtube"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="YouTube video URL"
                                        value={youtubeLink}
                                        onChange={(e) =>
                                            setYoutubeLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-[#ff0000] text-white border-none outline-none flex-1 placeholder-white rounded-2xl"
                                    />
                                </div>
                                {/* Instagram */}
                                <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-lg px-4 py-3">
                                    <span className="text-white text-2xl">
                                        <i className="fab fa-instagram"></i>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Instagram post or reel URL"
                                        aria-label="Instagram post, Reel, or profile URL"
                                        title="Public post and Reel links display an embed. Profile links open on Instagram."
                                        value={instagramLink}
                                        onChange={(e) =>
                                            setInstagramLink(e.target.value)
                                        }
                                        disabled={submitted && !isEditing}
                                        className="bg-transparent text-white border-none outline-none flex-1 placeholder-white rounded-2xl"
                                    />
                                </div>
                            </div>
                            </div>
                            )}
                            {/* {status !== 'pending' && status !== 'approved' && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSaveSection}
                    className="aqi-primary-action rounded-2xl bg-navy px-6 py-2 text-sm font-semibold text-white"
                  >
                    Save
                  </button>
                </div>
              )} */}
                        </div>
                    )}

                    {/* Legal Docs */}
                    {activeTab === 5 && (
                        <div className="space-y-6">
                            <h2 className="text-[#8B1C2D] font-bold text-xl">LEGAL & FINANCIALS</h2>

                            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                                <div className="rounded-2xl border border-[#e3eaf3] bg-white p-5">
                                    <label className="mb-2 block font-semibold text-navy">Form C (EDGAR filing)</label>
                                    {(!submitted || isEditing) ? (
                                        <input
                                            type="url"
                                            value={legalUploads.edgar_filing_url || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLegalUploads((prev) => ({ ...prev, edgar_filing_url: value }));
                                                if (edgarFilingUrlError) {
                                                    setEdgarFilingUrlError(getEdgarFilingUrlError(value));
                                                }
                                            }}
                                            onBlur={(e) =>
                                                setEdgarFilingUrlError(
                                                    getEdgarFilingUrlError(
                                                        e.target.value,
                                                    ),
                                                )
                                            }
                                            placeholder="https://www.sec.gov/Archives/edgar/data/..."
                                            aria-invalid={Boolean(edgarFilingUrlError)}
                                            aria-describedby="edgar-filing-url-error"
                                            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-navy ${
                                                edgarFilingUrlError
                                                    ? "border-red-500"
                                                    : "border-navy/25"
                                            }`}
                                        />
                                    ) : legalUploads.edgar_filing_url ? (
                                        <a href={getLegalDocumentUrl(legalUploads.edgar_filing_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-navy underline">
                                            View Form C filing <i className="fas fa-external-link-alt text-xs"></i>
                                        </a>
                                    ) : <p className="text-sm italic text-gray-500">No EDGAR filing link added.</p>}
                                    {edgarFilingUrlError && (
                                        <p
                                            id="edgar-filing-url-error"
                                            className="mt-2 text-xs text-red-600"
                                        >
                                            {edgarFilingUrlError}
                                        </p>
                                    )}
                                </div>

                                {[
                                    { key: "subscription_agreement", label: "Subscription Agreement" },
                                    { key: "financials", label: "Reviewed/Audited Financials" },
                                ].map(({ key, label }) => (
                                    <div key={key} className="rounded-2xl border border-[#e3eaf3] bg-white p-5">
                                        <p className="mb-2 font-semibold text-navy">{label}</p>
                                        {(!submitted || isEditing) && (
                                            <label className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-navy/30 bg-[#f0f6fb] px-4 py-2 text-sm font-medium text-navy hover:bg-[#e3eef8]">
                                                <i className="fas fa-upload text-xs"></i> Upload document
                                                <input type="file" accept=".pdf,.xls,.xlsx,.doc,.docx" multiple className="hidden" onChange={(e) => handleLegalUpload(key, e.target.files)} />
                                            </label>
                                        )}
                                        {legalUploading[key] && <p className="mb-2 text-sm text-gray-500">Uploading...</p>}
                                        <div className="space-y-2">
                                            {(legalFiles[key] || []).map((file, idx) => (
                                                <div key={`new-${idx}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#F5F7FA] px-3 py-2 text-sm">
                                                    <span className="min-w-0 truncate text-navy">{file.name}</span>
                                                    <button type="button" aria-label={`Remove ${file.name}`} className="text-red-500" onClick={() => setLegalFiles((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }))}>×</button>
                                                </div>
                                            ))}
                                            {(legalUploads[key] || []).map((document, idx) => (
                                                <div key={`saved-${idx}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#F5F7FA] px-3 py-2 text-sm">
                                                    <a href={getLegalDocumentUrl(document)} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate font-medium text-navy underline">
                                                        {getLegalDocumentName(document, `${label} ${idx + 1}`)}
                                                    </a>
                                                    {(!submitted || isEditing) && <button type="button" aria-label="Delete document" className="text-red-500" onClick={() => handleDeleteLegalDocument(key, idx)}>×</button>}
                                                </div>
                                            ))}
                                            {!legalFiles[key]?.length && !legalUploads[key]?.length && <p className="text-sm italic text-gray-500">No document uploaded.</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="rounded-2xl border border-[#e3eaf3] bg-white p-6">
                                <div className="mb-4 flex items-center justify-between gap-3">
                                    <h3 className="font-semibold text-navy">Risks</h3>
                                    <span className="text-xs text-gray-500">Up to 10 risks</span>
                                </div>
                                <div className="space-y-3">
                                    {(legalUploads.risks || [""]).map((risk, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <span className="pt-2 text-sm font-semibold text-[#8B1C2D]">{idx + 1}.</span>
                                            {(!submitted || isEditing) ? (
                                                <>
                                                    <textarea
                                                        value={risk}
                                                        maxLength={1000}
                                                        rows={3}
                                                        onChange={(e) => setLegalUploads((prev) => ({ ...prev, risks: prev.risks.map((item, i) => i === idx ? e.target.value : item) }))}
                                                        placeholder="Describe a material investment risk"
                                                        className="flex-1 resize-y rounded-lg border border-navy/20 px-3 py-2 text-sm outline-none focus:border-navy"
                                                    />
                                                    {(legalUploads.risks || []).length > 1 && <button type="button" className="pt-2 text-red-500" onClick={() => setLegalUploads((prev) => ({ ...prev, risks: prev.risks.filter((_, i) => i !== idx) }))}>×</button>}
                                                </>
                                            ) : <p className="whitespace-pre-wrap py-2 text-sm text-gray-700">{risk}</p>}
                                        </div>
                                    ))}
                                </div>
                                {(!submitted || isEditing) && (legalUploads.risks || []).length < 10 && (
                                    <button type="button" className="mt-4 text-sm font-semibold text-navy" onClick={() => setLegalUploads((prev) => ({ ...prev, risks: [...(prev.risks || []), ""] }))}>+ Add risk</button>
                                )}
                            </div>

                            <div className="rounded-2xl border border-[#e3eaf3] bg-white p-6">
                                <label className="mb-3 block font-semibold text-navy">Use of Funds</label>
                                {(!submitted || isEditing) ? (
                                    <textarea
                                        value={legalUploads.use_of_funds || ""}
                                        maxLength={5000}
                                        rows={10}
                                        onChange={(e) => setLegalUploads((prev) => ({ ...prev, use_of_funds: e.target.value }))}
                                        placeholder="Explain how the proceeds from this offering will be used"
                                        className="w-full resize-y rounded-lg border border-navy/20 px-4 py-3 text-sm outline-none focus:border-navy"
                                    />
                                ) : <p className="whitespace-pre-wrap text-sm leading-7 text-gray-700">{legalUploads.use_of_funds || "No use of funds information added."}</p>}
                            </div>
                        </div>
                    )}

                    {/* Legacy legal document layout retained for historical data compatibility. */}
                    {activeTab === -5 && (
                        <div className="space-y-6">
                            <h2 className="text-[#8B1C2D] font-bold text-xl mb-4">
                                LEGAL & FINANCIALS
                            </h2>
                            <div className="space-y-5">

                                {/* Form C */}
                                <div className="border border-[#e3eaf3] rounded-2xl p-6 bg-white">
                                    <p className="text-navy font-semibold text-base mb-4">Form C</p>
                                    {(!submitted || isEditing) && (
                                        <label className="cursor-pointer">
                                            <input
                                                type="file"
                                                accept="application/pdf,.pdf,.xls,.xlsx,.docx,.doc,image/*"
                                                multiple
                                                onChange={(e) => handleLegalUpload("form_c", e.target.files)}
                                                className="hidden"
                                            />
                                            <div className="inline-flex items-center gap-2 bg-[#f0f6fb] border border-navy/30 rounded-lg px-4 py-2 hover:bg-[#e3eef8] transition-colors cursor-pointer select-none">
                                                <i className="fas fa-upload text-navy text-xs"></i>
                                                <span className="text-navy font-medium text-sm">Choose Files</span>
                                            </div>
                                        </label>
                                    )}
                                    {legalUploading.form_c && (
                                        <p className="text-sm text-gray-500 mt-2">Uploading...</p>
                                    )}
                                    {/* Newly selected files */}
                                    {Array.isArray(legalFiles.form_c) && legalFiles.form_c.length > 0 && (
                                        <div className="flex flex-wrap gap-4 mt-5">
                                            {legalFiles.form_c.map((file, idx) => {
                                                if (!file) return null;
                                                const isImage = file.type && file.type.startsWith("image/");
                                                const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
                                                const isExcel = file.name?.toLowerCase().match(/\.(xls|xlsx)$/);
                                                const isWord = file.name?.toLowerCase().match(/\.(doc|docx)$/);
                                                const url = URL.createObjectURL(file);
                                                if (isImage) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div
                                                                className="w-28 h-28 rounded-xl overflow-hidden border border-navy/20 shadow-sm cursor-pointer"
                                                                onClick={() => { setActiveImage(url); setActivePdf(null); setModalOpen(true); }}
                                                            >
                                                                <img src={url} alt={`Form C ${idx + 1}`} className="w-full h-full object-cover" />
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, form_c: prev.form_c.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else if (isPdf) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm cursor-pointer bg-white flex flex-col items-center justify-center gap-1" onClick={() => { setActivePdf(url); setActiveImage(null); setModalOpen(true); }}>
                                                                <i className="fas fa-file-pdf text-4xl text-navy"></i>
                                                                <span className="text-xs text-navy font-medium text-center px-1 truncate w-full">{file.name}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, form_c: prev.form_c.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else {
                                                    const icon = isExcel ? "fa-file-excel" : isWord ? "fa-file-word" : "fa-file-alt";
                                                    const color = isExcel ? "text-green-600" : isWord ? "text-blue-600" : "text-gray-500";
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm bg-white flex flex-col items-center justify-center gap-1 px-1">
                                                                <i className={`fas ${icon} text-4xl ${color}`}></i>
                                                                <span className="text-xs text-gray-600 font-medium text-center truncate w-full">{file.name}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, form_c: prev.form_c.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    )}
                                    {/* Already uploaded files */}
                                    {Array.isArray(legalUploads.form_c) && legalUploads.form_c.length > 0 && (
                                        <div className="flex flex-wrap gap-4 mt-5">
                                            {legalUploads.form_c.map((filePath, idx) => {
                                                if (!filePath) return null;
                                                const url = filePath.startsWith("http")
                                                    ? filePath
                                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${filePath}`;
                                                const fileName = filePath.split("/").pop();
                                                if (filePath.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl overflow-hidden border border-navy/20 shadow-sm cursor-pointer" onClick={() => { setActiveImage(url); setActivePdf(null); setModalOpen(true); }}>
                                                                <img src={url} alt={`Form C ${idx + 1}`} className="w-full h-full object-cover" />
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("form_c", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else if (filePath.match(/\.pdf$/i)) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm cursor-pointer bg-white flex flex-col items-center justify-center gap-1" onClick={() => { setActivePdf(url); setActiveImage(null); setModalOpen(true); }}>
                                                                <i className="fas fa-file-pdf text-4xl text-navy"></i>
                                                                <span className="text-xs text-navy font-medium text-center px-1 truncate w-full">{fileName}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("form_c", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else {
                                                    const isExcel = filePath.match(/\.(xls|xlsx)$/i);
                                                    const isWord = filePath.match(/\.(doc|docx)$/i);
                                                    const icon = isExcel ? "fa-file-excel" : isWord ? "fa-file-word" : "fa-file-alt";
                                                    const color = isExcel ? "text-green-600" : isWord ? "text-blue-600" : "text-gray-500";
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <a href={url} target="_blank" rel="noopener noreferrer" className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm bg-white flex flex-col items-center justify-center gap-1 px-1 hover:bg-gray-50 transition-colors">
                                                                <i className={`fas ${icon} text-4xl ${color}`}></i>
                                                                <span className="text-xs text-gray-600 font-medium text-center truncate w-full">{fileName}</span>
                                                            </a>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("form_c", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Subscription Agreement */}
                                <div className="border border-[#e3eaf3] rounded-2xl p-6 bg-white">
                                    <p className="text-navy font-semibold text-base mb-4">Subscription Agreement</p>
                                    {(!submitted || isEditing) && (
                                        <label className="cursor-pointer">
                                            <input
                                                type="file"
                                                accept="application/pdf,.pdf,.xls,.xlsx,.docx,.doc,image/*"
                                                multiple
                                                onChange={(e) => handleLegalUpload("subscription_agreement", e.target.files)}
                                                className="hidden"
                                            />
                                            <div className="inline-flex items-center gap-2 bg-[#f0f6fb] border border-navy/30 rounded-lg px-4 py-2 hover:bg-[#e3eef8] transition-colors cursor-pointer select-none">
                                                <i className="fas fa-upload text-navy text-xs"></i>
                                                <span className="text-navy font-medium text-sm">Choose Files</span>
                                            </div>
                                        </label>
                                    )}
                                    {legalUploading.subscription_agreement && (
                                        <p className="text-sm text-gray-500 mt-2">Uploading...</p>
                                    )}
                                    {/* Newly selected files */}
                                    {Array.isArray(legalFiles.subscription_agreement) && legalFiles.subscription_agreement.length > 0 && (
                                        <div className="flex flex-wrap gap-4 mt-5">
                                            {legalFiles.subscription_agreement.map((file, idx) => {
                                                if (!file) return null;
                                                const isImage = file.type && file.type.startsWith("image/");
                                                const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
                                                const isExcel = file.name?.toLowerCase().match(/\.(xls|xlsx)$/);
                                                const isWord = file.name?.toLowerCase().match(/\.(doc|docx)$/);
                                                const url = URL.createObjectURL(file);
                                                if (isImage) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl overflow-hidden border border-navy/20 shadow-sm cursor-pointer" onClick={() => { setActiveImage(url); setActivePdf(null); setModalOpen(true); }}>
                                                                <img src={url} alt={`Subscription Agreement ${idx + 1}`} className="w-full h-full object-cover" />
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, subscription_agreement: prev.subscription_agreement.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else if (isPdf) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm cursor-pointer bg-white flex flex-col items-center justify-center gap-1" onClick={() => { setActivePdf(url); setActiveImage(null); setModalOpen(true); }}>
                                                                <i className="fas fa-file-pdf text-4xl text-navy"></i>
                                                                <span className="text-xs text-navy font-medium text-center px-1 truncate w-full">{file.name}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, subscription_agreement: prev.subscription_agreement.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else {
                                                    const icon = isExcel ? "fa-file-excel" : isWord ? "fa-file-word" : "fa-file-alt";
                                                    const color = isExcel ? "text-green-600" : isWord ? "text-blue-600" : "text-gray-500";
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm bg-white flex flex-col items-center justify-center gap-1 px-1">
                                                                <i className={`fas ${icon} text-4xl ${color}`}></i>
                                                                <span className="text-xs text-gray-600 font-medium text-center truncate w-full">{file.name}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => setLegalFiles((prev) => ({ ...prev, subscription_agreement: prev.subscription_agreement.filter((_, i) => i !== idx) }))}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    )}
                                    {/* Already uploaded files */}
                                    {Array.isArray(legalUploads.subscription_agreement) && legalUploads.subscription_agreement.length > 0 && (
                                        <div className="flex flex-wrap gap-4 mt-5">
                                            {legalUploads.subscription_agreement.map((filePath, idx) => {
                                                if (!filePath) return null;
                                                const url = filePath.startsWith("http")
                                                    ? filePath
                                                    : `${process.env.REACT_APP_BACKEND_URL}/uploads/${filePath}`;
                                                const fileName = filePath.split("/").pop();
                                                if (filePath.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl overflow-hidden border border-navy/20 shadow-sm cursor-pointer" onClick={() => { setActiveImage(url); setActivePdf(null); setModalOpen(true); }}>
                                                                <img src={url} alt={`Subscription Agreement ${idx + 1}`} className="w-full h-full object-cover" />
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("subscription_agreement", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else if (filePath.match(/\.pdf$/i)) {
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <div className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm cursor-pointer bg-white flex flex-col items-center justify-center gap-1" onClick={() => { setActivePdf(url); setActiveImage(null); setModalOpen(true); }}>
                                                                <i className="fas fa-file-pdf text-4xl text-navy"></i>
                                                                <span className="text-xs text-navy font-medium text-center px-1 truncate w-full">{fileName}</span>
                                                            </div>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("subscription_agreement", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                } else {
                                                    const isExcel = filePath.match(/\.(xls|xlsx)$/i);
                                                    const isWord = filePath.match(/\.(doc|docx)$/i);
                                                    const icon = isExcel ? "fa-file-excel" : isWord ? "fa-file-word" : "fa-file-alt";
                                                    const color = isExcel ? "text-green-600" : isWord ? "text-blue-600" : "text-gray-500";
                                                    return (
                                                        <div key={idx} className="relative">
                                                            <a href={url} target="_blank" rel="noopener noreferrer" className="w-28 h-28 rounded-xl border border-navy/20 shadow-sm bg-white flex flex-col items-center justify-center gap-1 px-1 hover:bg-gray-50 transition-colors">
                                                                <i className={`fas ${icon} text-4xl ${color}`}></i>
                                                                <span className="text-xs text-gray-600 font-medium text-center truncate w-full">{fileName}</span>
                                                            </a>
                                                            {(!submitted || isEditing) && (
                                                                <button type="button" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 text-white flex items-center justify-center text-xs font-bold shadow leading-none" onClick={() => handleDeleteLegalDocument("subscription_agreement", idx)}>×</button>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Chat Section - Temporarily hidden but functionality preserved */}
                    {activeTab === 6 && (
                        <div className="space-y-6" style={{ display: "none" }}>
                            <h2 className="text-[#8B1C2D] font-bold text-xl">
                                FOUNDER CHAT
                            </h2>
                            <button
                                onClick={() =>
                                    navigate(`/campaigns/view/${campaignId}#CHAT`)
                                }
                                className="text-navy italic underline text-lg cursor-pointer bg-transparent border-0 text-left"
                                style={{ marginLeft: "2rem" }}
                            >
                                LINK TO CHAT WITH THE FOUNDER
                            </button>
                        </div>
                    )}
                </div>
                {/* RIGHT COLUMN */}
                <div className="w-full md:w-1/3 flex flex-col gap-4">
                    {submitted && !isEditing ? (
                        /* ── Read-only view ── */
                        <div className="border border-[#e3eaf3] rounded-2xl bg-white min-h-64 sm:min-h-80 md:min-h-96 lg:min-h-[400px] xl:min-h-[450px] 2xl:min-h-[500px]">
                            {draftSubmitted && (
                                <div className="px-5 py-3 bg-amber-50 rounded-t-2xl flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
                                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Submitted Changes — Pending Admin Review</span>
                                </div>
                            )}
                            <div className="px-5 py-4">
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {[
                                        { label: "Campaign Name", value: campaignName },
                                        { label: "Start Date", value: campaignStartDate },
                                        { label: "End Date", value: campaignEndDate },
                                        { label: "Goal Hit", value: goalHit },
                                        { label: "Max Amount", value: maxAmount },
                                        { label: "Min Amount", value: minAmount },
                                        { label: "Valuation", value: legalUploads.investment_valuation || "N/A" },
                                        { label: "Unit Price", value: unitPrice },
                                    ].map(({ label, value }) => (
                                        <div key={label}>
                                            <dt className="text-[10px] font-semibold text-[#6b7a8f] uppercase tracking-wide mb-0.5">{label}</dt>
                                            <dd className="text-[#23304a] text-sm font-medium">{value || <span className="text-gray-300 italic">—</span>}</dd>
                                        </div>
                                    ))}
                                </dl>
                                {normalizeInvestorPerks(legalUploads.investor_perks).some((perk) => perk.amount || perk.description) && (
                                    <div className="mt-4 border-t border-[#e3eaf3] pt-4">
                                        <p className="mb-2 font-semibold text-navy">Investor Perks</p>
                                        <ul className="space-y-2">
                                            {normalizeInvestorPerks(legalUploads.investor_perks)
                                                .filter((perk) => perk.amount || perk.description)
                                                .slice(0, showAllCampaignPerks ? undefined : 1)
                                                .map((perk, idx) => (
                                                    <li key={idx} className="text-sm text-[#23304a]">
                                                        {perk.amount && <strong className="mr-2">{formatInvestorPerkAmount(perk.amount)}</strong>}
                                                        {perk.description}
                                                    </li>
                                                ))}
                                        </ul>
                                        {normalizeInvestorPerks(legalUploads.investor_perks)
                                            .filter((perk) => perk.amount || perk.description).length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => setShowAllCampaignPerks((expanded) => !expanded)}
                                                className="mt-3 text-sm font-semibold text-navy"
                                            >
                                                {showAllCampaignPerks
                                                    ? "Show less"
                                                    : `See all ${normalizeInvestorPerks(legalUploads.investor_perks).filter((perk) => perk.amount || perk.description).length} perks`}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {campaignDescription && (
                                    <div className="mt-4">
                                        <dt className="text-[10px] font-semibold text-[#6b7a8f] uppercase tracking-wide mb-1">Campaign Description</dt>
                                        <p className="text-[#23304a] text-sm whitespace-pre-wrap leading-relaxed">{campaignDescription}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* ── Edit view ── */
                        <>
                            {/* Campaign Name */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    CAMPAIGN NAME
                                </label>
                                <input
                                    type="text"
                                    value={campaignName}
                                    onChange={(e) => setCampaignName(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                />
                            </div>

                            {/* Campaign Description */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    CAMPAIGN DESCRIPTION
                                </label>
                                <textarea
                                    value={campaignDescription}
                                    onChange={(e) => setCampaignDescription(e.target.value)}
                                    maxLength={maxDescriptionLength}
                                    className="w-full h-40 border border-navy rounded-2xl p-3 focus:outline-none text-sm"
                                />
                                <div className="text-sm text-gray-500 mt-1">
                                    Text counter length –{" "}
                                    {maxDescriptionLength - campaignDescription.length}
                                </div>
                            </div>


                            {/* Campaign Start Date */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    CAMPAIGN START DATE
                                </label>
                                <input
                                    type="date"
                                    value={campaignStartDate || ""}
                                    onChange={(e) => {
                                        const selectedDate = new Date(e.target.value);
                                        const today = new Date();
                                        const maxDate = new Date();
                                        maxDate.setMonth(maxDate.getMonth() + 12);

                                        // Only set the date if it's valid
                                        if (
                                            selectedDate >= today &&
                                            selectedDate <= maxDate
                                        ) {
                                            setCampaignStartDate(e.target.value);
                                        }
                                    }}
                                    min={new Date().toISOString().split("T")[0]}
                                    max={
                                        new Date(
                                            new Date().setMonth(new Date().getMonth() + 12),
                                        )
                                            .toISOString()
                                            .split("T")[0]
                                    }
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                />
                                <span className="text-sm text-gray-500 mt-1">
                                    Select a date between today and 12 months from now
                                </span>
                            </div>

                            {/* Campaign End Date */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    CAMPAIGN END DATE
                                </label>
                                <input
                                    type="date"
                                    value={campaignEndDate}
                                    onChange={(e) => {
                                        const selectedDate = new Date(e.target.value);
                                        const today = new Date();
                                        const maxDate = new Date();
                                        maxDate.setMonth(maxDate.getMonth() + 12);

                                        // Only set the date if it's valid
                                        if (
                                            selectedDate >= today &&
                                            selectedDate <= maxDate
                                        ) {
                                            setCampaignEndDate(e.target.value);
                                        }
                                    }}
                                    min={new Date().toISOString().split("T")[0]}
                                    max={
                                        new Date(
                                            new Date().setMonth(new Date().getMonth() + 12),
                                        )
                                            .toISOString()
                                            .split("T")[0]
                                    }
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                />
                                <span className="text-sm text-gray-500 mt-1">
                                    Select a date between today and 12 months from now
                                </span>
                            </div>


                            {/* Goal Hit */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    GOAL HIT
                                </label>
                                <input
                                    type="number"
                                    value={goalHit}
                                    onChange={(e) => setGoalHit(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                    min="0"
                                />
                            </div>


                            {/* Target Amount */}
                            <div>
                                <label className="text-navy font-semibold block mb-1 flex items-center gap-2">
                                    MAX AMOUNT
                                    <InfoTooltip
                                        id="target-amount-info"
                                        content={
                                            <span>
                                                <strong>Raising Between $124,000 and $1,235,000:</strong> Companies raising between $124,000 and $1,235,000 must provide financial statements that are reviewed by a public accountant who is independent of the company. A review is less rigorous than an audit but still requires a certified accountant to verify that the company’s financials follow generally accepted accounting principles (GAAP).<br /><br />
                                                <strong>Raising More Than $1,235,000:</strong> If a company is seeking to raise more than $1,235,000, it must provide audited financial statements. These financials must be prepared in accordance with GAAP and reviewed by an independent auditor. This is one of the more stringent requirements of Reg CF and often presents a challenge for smaller companies with limited budgets for professional audits.
                                            </span>
                                        }
                                    />
                                </label>
                                <input
                                    type="number"
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                    min="0"
                                />
                            </div>

                            {/* Min Amount */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    MIN AMOUNT
                                </label>
                                <input
                                    type="number"
                                    value={minAmount}
                                    onChange={(e) => setMinAmount(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                    min="0"
                                    step="any"
                                />
                            </div>

                            
                            {/* Minimum Raised */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    MINIMUM TARGET
                                </label>
                                <input
                                    type="number"
                                    value={minimumRaised}
                                    onChange={(e) => setMinimumRaised(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                    min="0"
                                    step="any"
                                />
                            </div>

                            <div className="border-t border-[#e3eaf3] pt-5">
                                <h2 className="mb-4 text-xl font-bold text-[#8B1C2D]">INVESTMENT TERMS</h2>
                                <label className="text-navy font-semibold block mb-1">
                                    VALUATION
                                </label>
                                <input
                                    type="text"
                                    value={legalUploads.investment_valuation || ""}
                                    onChange={(e) => setLegalUploads((prev) => ({ ...prev, investment_valuation: e.target.value }))}
                                    placeholder="N/A"
                                    maxLength={50}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                />
                                <p className="mt-1 text-xs text-gray-500">Leave blank to display N/A.</p>
                            </div>

                            {/* Unit Price */}
                            <div>
                                <label className="text-navy font-semibold block mb-1">
                                    PRICE PER SHARE
                                </label>
                                <input
                                    type="number"
                                    value={unitPrice}
                                    onChange={(e) => setUnitPrice(e.target.value)}
                                    className="w-full border border-navy rounded-2xl px-3 py-2 focus:outline-none"
                                    min="0"
                                    step="any"
                                />
                            </div>

                            <div className="rounded-2xl border border-[#e3eaf3] bg-white p-4">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <h3 className="font-semibold text-navy">Investor Perks</h3>
                                    <span className="text-xs text-gray-500">Up to 5 perks</span>
                                </div>
                                <div className="space-y-4">
                                    {normalizeInvestorPerks(legalUploads.investor_perks)
                                        .slice(0, showAllCampaignPerks ? undefined : 1)
                                        .map((perk, idx) => (
                                        <div key={idx} className="rounded-xl bg-[#F5F7FA] p-3">
                                            <div className="mb-2 flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={perk.amount}
                                                    onChange={(e) => setLegalUploads((prev) => ({
                                                        ...prev,
                                                        investor_perks: normalizeInvestorPerks(prev.investor_perks).map((item, i) => i === idx ? { ...item, amount: e.target.value.replace(/\D/g, "") } : item),
                                                    }))}
                                                    placeholder="Investment amount (e.g. 250)"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    maxLength={30}
                                                    className="h-11 min-w-0 flex-1 rounded-lg border border-navy/30 px-3 py-2 text-sm outline-none focus:border-navy"
                                                />
                                                {normalizeInvestorPerks(legalUploads.investor_perks).length > 1 && (
                                                    <button type="button" aria-label="Remove perk" className="text-lg text-red-500" onClick={() => setLegalUploads((prev) => ({ ...prev, investor_perks: normalizeInvestorPerks(prev.investor_perks).filter((_, i) => i !== idx) }))}>×</button>
                                                )}
                                            </div>
                                            <textarea
                                                value={perk.description}
                                                onChange={(e) => setLegalUploads((prev) => ({
                                                    ...prev,
                                                    investor_perks: normalizeInvestorPerks(prev.investor_perks).map((item, i) => i === idx ? { ...item, description: e.target.value } : item),
                                                }))}
                                                placeholder="Describe what the investor receives"
                                                maxLength={500}
                                                rows={1}
                                                className="h-11 w-full resize-none rounded-lg border border-navy/30 px-3 py-2 text-sm outline-none focus:border-navy"
                                            />
                                        </div>
                                    ))}
                                </div>
                                {normalizeInvestorPerks(legalUploads.investor_perks).length > 1 && (
                                    <button
                                        type="button"
                                        className="mt-3 mr-4 text-sm font-semibold text-navy"
                                        onClick={() => setShowAllCampaignPerks((expanded) => !expanded)}
                                    >
                                        {showAllCampaignPerks
                                            ? "Show less"
                                            : `See all ${normalizeInvestorPerks(legalUploads.investor_perks).length} perks`}
                                    </button>
                                )}
                                {normalizeInvestorPerks(legalUploads.investor_perks).length < 5 && (
                                    <button
                                        type="button"
                                        className="mt-3 text-sm font-semibold text-navy"
                                        onClick={() => {
                                            setLegalUploads((prev) => ({
                                                ...prev,
                                                investor_perks: [
                                                    ...normalizeInvestorPerks(prev.investor_perks),
                                                    { amount: "", description: "" },
                                                ],
                                            }));
                                            setShowAllCampaignPerks(true);
                                        }}
                                    >
                                        + Add perk
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {/* Invest Section */}
                    <div className="mt-4 w-full">
                        {status === "pending" ? (
                            <div className="text-center text-green-600 font-semibold mt-2">
                                Submitted for Review
                            </div>
                        ) : draftSubmitted ? (
                            <div className="text-center text-green-600 font-semibold mt-2">
                                Changes submitted, waiting for admin review
                            </div>
                        ) : isEditing ? (
                            <div className="flex flex-row gap-3 w-full mt-3">
                                {/* Cancel */}
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="w-1/2 flex items-center justify-center gap-2 bg-white border-2 border-[#e3eaf3] text-[#6b7a8f] py-2.5 rounded-2xl text-sm font-semibold hover:border-navy hover:text-navy transition-all duration-200 shadow-sm"
                                >
                                    <i className="fas fa-times text-xs"></i>
                                    Cancel
                                </button>
                                {/* Save / Submit */}
                                <button
                                    onClick={
                                        status === "approved" || status === "disabled"
                                            ? handleSubmitDraft
                                            : handleSaveSection
                                    }
                                    className="w-1/2 flex items-center justify-center gap-2 bg-navy text-white py-2.5 rounded-2xl text-sm font-semibold hover:bg-navy-2 transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                    <i className={`fas ${status === "approved" || status === "disabled" ? "fa-paper-plane" : "fa-save"} text-xs`}></i>
                                    {status === "approved" || status === "disabled" ? "Submit for Review" : "Save"}
                                </button>
                            </div>
                        ) : status === "approved" || (status === "disabled" && disabledEditEnabled) ? (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="mx-auto mt-3 flex items-center justify-center gap-2 bg-navy text-white px-8 py-2.5 rounded-2xl text-sm font-bold tracking-wide hover:bg-navy-2 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                                <i className="fas fa-pen text-xs"></i>
                                Edit Campaign
                            </button>
                        ) : status === "disabled" ? (
                            <div className="text-center text-gray-600 font-semibold mt-2">
                                Campaign editing is disabled.
                            </div>
                        ) : (
                            <div className="flex flex-row gap-3 w-full mt-3">
                                <button
                                    onClick={handleSaveSection}
                                    className="aqi-primary-action flex w-1/2 items-center justify-center gap-2 rounded-2xl border-2 border-navy bg-white py-2.5 text-sm font-semibold text-navy shadow-sm transition-all duration-200 hover:bg-[#f0f6fb]"
                                >
                                    <i className="fas fa-save text-xs"></i>
                                    Save Progress
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="aqi-primary-action flex w-1/2 items-center justify-center gap-2 rounded-2xl bg-navy py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-navy-2 hover:shadow-lg"
                                >
                                    <i className="fas fa-paper-plane text-xs"></i>
                                    Submit for Review
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showCropper && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center p-4">
                    <div className="relative bg-white p-4 rounded-xl w-full max-w-4xl h-[600px] flex flex-col">
                        {/* Cropper Canvas */}
                        <div className="relative flex-1">
                            <Cropper
                                image={croppingImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={21 / 9}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(croppedArea, croppedPixels) => {
                                    setCroppedAreaPixels(croppedPixels);
                                }}
                            />
                        </div>

                        {/* Crop guidance text */}
                        <p className="text-gray-600 text-sm mt-2">
                            Adjust the crop area to create a wide background image
                            (recommended aspect ratio 21:9). Use zoom controls to
                            fit your content properly within the frame.
                        </p>

                        {/* Action Buttons */}
                        <div className="mt-4 flex justify-between">
                            <button
                                onClick={() => setShowCropper(false)}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCropSave}
                                className="aqi-primary-action rounded bg-navy px-4 py-2 text-white"
                            >
                                Save Crop
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal for image/pdf preview - moved to root so it's always available */}
            {modalOpen && (activePdf || activeImage) && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                    {/* Modal Content */}
                    <div className="relative w-[95%] max-w-5xl bg-white p-4 rounded-xl shadow-lg">
                        {/* Close Button */}
                        <div className="flex justify-end mb-2">
                            <button
                                className="text-gray-600 hover:text-red-500 text-2xl font-bold focus:outline-none"
                                onClick={() => {
                                    setModalOpen(false);
                                    setActivePdf(null);
                                    setActiveImage(null);
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        {/* PDF or Image Display */}
                        {activePdf ? (
                            <iframe
                                src={activePdf}
                                title="Full PDF View"
                                className="w-full h-[80vh] border border-gray-300 rounded-lg"
                            />
                        ) : (
                            <img
                                src={activeImage}
                                alt="Preview"
                                className="w-full max-h-[80vh] object-contain rounded-lg border border-gray-300"
                            />
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

// Add missing closing brace for the component


CampaignAboutPage.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
    }),
};


export default CampaignAboutPage;
